/**
 * Algolia InstantSearch — /search results page.
 *
 * Multi-index tabbed architecture:
 *   ALL tab: products grid + preview rows for collections/recipes/blogs
 *   PRODUCTS tab: full filter/sort/pagination
 *   COLLECTIONS tab: collection cards
 *   RECIPES tab: recipe cards (blog.handle === "recipe")
 *   BLOGS tab: article cards (blog.handle !== "recipe")
 *   PAGES tab: page cards
 *
 * Depends on: algoliasearch v5 UMD, algolia-search-client.js
 */
(function () {
  'use strict';

  var config = window.__ALGOLIA_CONFIG;
  if (!config || !config.appId || config.appId.indexOf('REPLACE') === 0) {
    console.warn('[Algolia] Search credentials not configured. InstantSearch disabled.');
    return;
  }

  // Same fallback image the theme's own product cards use (snippets/card-product.liquid)
  var PLACEHOLDER_IMAGE = (function () {
    var src = (document.currentScript && document.currentScript.src) || '';
    return src.replace(/algolia-instantsearch\.js.*$/, 'default-magnolia.jpg');
  })();

  if (window.location.pathname !== '/search') return;

  var searchParams = new URLSearchParams(window.location.search);
  var query = searchParams.get('q') || '';
  var currentQuery = query;
  var currentTab = 'all';
  // ALL shares PRODUCTS' filter/sort/page bucket below; RECIPES/BLOGS each
  // keep their own. Resolve via getFilterScope()/getPageScope(), never a
  // literal tab name.
  var currentSort = {
    products: 'default',
    recipes: 'default',
    blogs: 'default',
  };
  var activeFilters = {
    products: {},
    recipes: {},
    blogs: {},
  };
  var currentPage = { products: 0, collections: 0, recipes: 0, blogs: 0, pages: 0 };
  // Each tab's zero-filter total — tab labels read only from here.
  var baselineCounts = { products: 0, collections: 0, recipes: 0, blogs: 0, pages: 0 };
  var expandedFacets = {};
  var collapsedFacets = {};
  var tabData = {};
  var searchClient = null;
  var isMobile = window.innerWidth < 750;
  // Last queryID an impression was reported for, per index. switchTab() runs on
  // every tab change and back-navigation; a result set must count as viewed
  // once, or impressions inflate and click-through rate collapses.
  var lastImpressionQueryID = {};

  // QueryID tracking for analytics attribution
  var queryIDsByIndex = {}; // Store queryID per index

  /**
   * Capture queryID from search response for analytics attribution
   */
  function captureQueryID(indexName, queryID) {
    if (queryID && window.AlgoliaInsightsUtils) {
      queryIDsByIndex[indexName] = queryID;
      window.AlgoliaInsightsUtils.setSearchAttribution(queryID, indexName);
    }
  }

  /**
   * Add click tracking to product cards after they're rendered
   * Attaches event listeners to track clicks with queryID and position
   */
  function addClickTrackingToProducts(container, indexName) {
    // Wait for AlgoliaInsightsUtils to load (max 5 seconds)
    var attempts = 0;
    var maxAttempts = 50; // 50 attempts × 100ms = 5 seconds

    function tryAddTracking() {
      if (window.AlgoliaInsightsUtils) {
        // Utils loaded, proceed with tracking
        var queryID = queryIDsByIndex[indexName];
        if (!queryID) {
          console.warn('[Algolia] No queryID available for click tracking');
          return;
        }

        addTrackingListeners(container, indexName, queryID);
      } else if (attempts < maxAttempts) {
        // Utils not loaded yet, retry in 100ms
        attempts++;
        setTimeout(tryAddTracking, 100);
      } else {
        // Timeout - utils didn't load
        console.warn('[Algolia] Insights utils not loaded after 5s, click tracking disabled');
      }
    }

    tryAddTracking();
  }

  function addTrackingListeners(container, indexName, queryID) {
    if (!queryID) {
      console.warn('[Algolia] No queryID available for click tracking');
      return;
    }

    var productCards = container.querySelectorAll('.hit-wrapper');

    productCards.forEach(function (card, index) {
      var handle = card.getAttribute('data-product-handle');
      if (!handle) return;

      var position = index + 1; // 1-based position
      var links = card.querySelectorAll('a[href*="/products/"]');

      links.forEach(function (link) {
        // Add queryID to URL for product page attribution
        var url = new URL(link.href);
        if (!url.searchParams.has('queryID')) {
          url.searchParams.set('queryID', queryID);
          link.href = url.toString();
        }

        // Track click event
        link.addEventListener('click', function () {
          window.AlgoliaInsightsUtils.trackClick(handle, indexName, position, queryID);
        });
      });
    });
  }

  // ─── URL state persistence ──────────────────────────────────────────
  // Tab, sort, page, and filters mirror into the URL on every change.
  // persistUrlState(push) pushes a history entry for user actions, replaces
  // otherwise. restoreUrlState() resets state first, safe to call again.

  function resetUrlState() {
    currentTab = 'all';
    Object.keys(currentSort).forEach(function (scope) {
      currentSort[scope] = 'default';
    });
    Object.keys(activeFilters).forEach(function (scope) {
      activeFilters[scope] = {};
    });
    Object.keys(currentPage).forEach(function (scope) {
      currentPage[scope] = 0;
    });
  }

  function restoreUrlState() {
    resetUrlState();

    var params = new URLSearchParams(window.location.search);
    var validTabs = { all: 1, products: 1, collections: 1, recipes: 1, blogs: 1, pages: 1 };
    var tabParam = params.get('tab');
    if (tabParam && validTabs[tabParam]) currentTab = tabParam;

    params.forEach(function (value, key) {
      if (key.indexOf('sort__') === 0) {
        var sortScope = key.slice('sort__'.length);
        if (Object.prototype.hasOwnProperty.call(currentSort, sortScope)) currentSort[sortScope] = value;
        return;
      }
      if (key.indexOf('page__') === 0) {
        var pageScope = key.slice('page__'.length);
        var pageNum = parseInt(value, 10);
        if (Object.prototype.hasOwnProperty.call(currentPage, pageScope) && !isNaN(pageNum) && pageNum > 0) {
          currentPage[pageScope] = pageNum;
        }
        return;
      }
      if (key.indexOf('f__') === 0) {
        var rest = key.slice('f__'.length);
        var sepIdx = rest.indexOf('__');
        if (sepIdx === -1) return;
        var filterScope = rest.slice(0, sepIdx);
        var attr = rest.slice(sepIdx + 2);
        if (!Object.prototype.hasOwnProperty.call(activeFilters, filterScope) || !attr) return;
        var values = value.split(',').filter(function (v) {
          return v;
        });
        if (values.length > 0) activeFilters[filterScope][attr] = values;
      }
    });
  }

  restoreUrlState();

  function persistUrlState(push) {
    var params = new URLSearchParams();
    if (currentQuery) params.set('q', currentQuery);
    if (currentTab && currentTab !== 'all') params.set('tab', currentTab);

    Object.keys(currentSort).forEach(function (scope) {
      if (currentSort[scope] && currentSort[scope] !== 'default') {
        params.set('sort__' + scope, currentSort[scope]);
      }
    });

    Object.keys(currentPage).forEach(function (scope) {
      if (currentPage[scope]) params.set('page__' + scope, String(currentPage[scope]));
    });

    Object.keys(activeFilters).forEach(function (scope) {
      var scopedFilters = activeFilters[scope];
      Object.keys(scopedFilters).forEach(function (attr) {
        var values = scopedFilters[attr];
        if (values && values.length > 0) {
          params.set('f__' + scope + '__' + attr, values.join(','));
        }
      });
    });

    var qs = params.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '');
    if (push) {
      window.history.pushState(null, '', newUrl);
    } else {
      window.history.replaceState(null, '', newUrl);
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────

  /* Start mobile facet filter and sort */
  // Mobile filter drawer navigation state: 'main' shows the list of facet
  // categories (Category, Color, Price, etc); 'facet' drills into a single
  // facet's checkbox list, mirroring the Shopify theme's collection page
  // filter drawer (see magnolia.com/collections/* for the reference UX).
  var drawerPanel = 'main'; // 'main' | 'facet'
  var drawerFacetAttr = null;

  // Unfiltered "total" item count per scope, e.g. the 24 in "20 of 24 Items".
  // Captured the moment a scope loads with zero active filters (including
  // the very first load) and left untouched while filters are applied —
  // Algolia's nbHits on a filtered query only reflects the filtered count.
  var tabTotalCounts = { products: null, recipes: null, blogs: null };

  /* function captureTotalCountIfUnfiltered(scope, data) {
    if (!data) return;
    var scopedFilters = activeFilters[scope] || {};
    if (Object.keys(scopedFilters).length === 0) {
      tabTotalCounts[scope] = data.nbHits;
    }
  } */

  function getTotalCount(scope, data) {
    if (tabTotalCounts[scope] != null) return tabTotalCounts[scope];
    return data ? data.nbHits : 0;
  }

  // "20 of 24 Items" once a filter narrows the set, otherwise just "24 Items".
  function formatItemCount(scope, data) {
    var filtered = data ? data.nbHits : 0;
    var total = getTotalCount(scope, data);
    var scopedFilters = activeFilters[scope] || {};
    var hasActiveFilters = Object.keys(scopedFilters).length > 0;
    var noun = 'Item' + (total === 1 ? '' : 's');
    if (hasActiveFilters && filtered !== total) {
      return filtered + ' of ' + total + ' ' + noun;
    }
    return total + ' ' + noun;
  }

  var FILTER_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-filter" viewBox="0 0 20 20"><path fill="currentColor" fill-rule="evenodd" d="M4.833 6.5a1.667 1.667 0 1 1 3.334 0 1.667 1.667 0 0 1-3.334 0M4.05 7H2.5a.5.5 0 0 1 0-1h1.55a2.5 2.5 0 0 1 4.9 0h8.55a.5.5 0 0 1 0 1H8.95a2.5 2.5 0 0 1-4.9 0m11.117 6.5a1.667 1.667 0 1 0-3.334 0 1.667 1.667 0 0 0 3.334 0M13.5 11a2.5 2.5 0 0 1 2.45 2h1.55a.5.5 0 0 1 0 1h-1.55a2.5 2.5 0 0 1-4.9 0H2.5a.5.5 0 0 1 0-1h8.55a2.5 2.5 0 0 1 2.45-2"></path></svg>';
  /* '<svg viewBox="0 0 18 18" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" aria-hidden="true">' +
    '<line x1="2" y1="4" x2="16" y2="4"/><circle cx="11" cy="4" r="1.6" fill="currentColor" stroke="none"/>' +
    '<line x1="2" y1="9" x2="16" y2="9"/><circle cx="6" cy="9" r="1.6" fill="currentColor" stroke="none"/>' +
    '<line x1="2" y1="14" x2="16" y2="14"/><circle cx="13" cy="14" r="1.6" fill="currentColor" stroke="none"/>' +
    '</svg>'; */
  /* End mobile facet filter and sort */

  function formatPrice(price) {
    return '$' + parseFloat(price).toFixed(2);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    // Text-node escaping skips quotes, but every caller uses this inside a
    // double-quoted attribute, so raw " in the value (e.g. 6" dimensions)
    // would terminate the attribute early.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ─── Shopify product data fetch (shared by option hints + image carousel) ──
  var productDataCache = {};

  function pluralize(word) {
    var w = typeof word === 'string' ? word : String(word || '');
    if (!w || w.slice(-1) === 's') return w;
    if (w.slice(-2) === 'sh' || w.slice(-2) === 'ch' || w.slice(-1) === 'x' || w.slice(-1) === 'z') return w + 'es';
    return w + 's';
  }

  function fetchProductData(handle) {
    if (productDataCache[handle] !== undefined) {
      return Promise.resolve(productDataCache[handle]);
    }
    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (product) {
        // Unwrap { product: { ... } } shape if present (admin JSON format)
        var data = product && product.product ? product.product : product;
        productDataCache[handle] = data;
        return data;
      })
      .catch(function () {
        productDataCache[handle] = null;
        return null;
      });
  }

  function fetchProductOptions(handle) {
    return fetchProductData(handle).then(function (data) {
      var rawOptions = data && Array.isArray(data.options) ? data.options : [];
      var variants = data && Array.isArray(data.variants) ? data.variants : [];

      // Handle each option element individually — .js returns strings, .json returns objects
      return rawOptions.map(function (opt, i) {
        var name, count;
        if (opt !== null && typeof opt === 'object') {
          // Object: { name: "Size", values: ["S","M","L"] }
          name = typeof opt.name === 'string' ? opt.name : String(opt.name || '');
          count = Array.isArray(opt.values) ? opt.values.length : 0;
        } else {
          // String: "Size" — count unique values across variants
          name = typeof opt === 'string' ? opt : String(opt || '');
          count = 0;
        }
        // Fallback: count unique values from variants when count is still 0
        if (count === 0 && variants.length > 0) {
          var key = 'option' + (i + 1);
          var seen = {};
          variants.forEach(function (v) {
            if (v[key]) seen[v[key]] = true;
          });
          count = Object.keys(seen).length;
        }
        return { name: name, count: count };
      });
    });
  }

  // Fetches one card's real option counts from Shopify and updates its
  // [data-option-hint] span. Called per-card by lazyEnhanceCards() below,
  // once that card is actually near the viewport.
  function enhanceOptionHintsForCard(card, handle) {
    fetchProductOptions(handle).then(function (opts) {
      var hintEl = card.querySelector('[data-option-hint]');
      if (!hintEl) return;
      var multiOpts = opts.filter(function (o) {
        return o.count > 1;
      });
      if (multiOpts.length === 0) {
        hintEl.style.display = 'none';
        return;
      }
      var parts = multiOpts.map(function (o) {
        return o.count + ' ' + pluralize(o.name);
      });
      hintEl.textContent = parts.join(', ');
      hintEl.style.display = '';
    });
  }

  /* START - code for the product carousel dots and arrow for each products*/
  // ─── Product image carousel ────────────────────────────────────────
  // Cards render with a single static image first (fast paint), then this
  // fetches /products/{handle}.js via fetchProductData, pulling in the
  // product's full image list to build a prev/next + dots carousel.
  // Cards with fewer than 2 images are left as a plain image.

  var CAROUSEL_MAX_IMAGES = 6;

  function injectCarouselStyles() {
    if (document.getElementById('algolia-carousel-styles')) return;
    var style = document.createElement('style');
    style.id = 'algolia-carousel-styles';
    style.textContent =
      '.hit-image{position:relative;}' +
      '.hit-carousel__arrow{position:absolute;top:50%;transform:translateY(-50%);' +
      'width:32px;height:32px;border-radius:50%;background:#fff;border:none;' +
      'display:flex;align-items:center;justify-content:center;cursor:pointer;' +
      'box-shadow:0 1px 4px rgba(0,0,0,.2);z-index:2;padding:0;color:#333;' +
      'opacity:0;pointer-events:none;transition:opacity .15s ease;}' +
      '.hit-carousel__arrow--prev{left:8px;}' +
      '.hit-carousel__arrow--next{right:8px;}' +
      '.hit-carousel__arrow svg{width:16px;height:16px;}' +
      '.hit-carousel__dots{position:absolute;left:0;right:0;bottom:45px;' +
      'display:flex;justify-content:center;gap:6px;z-index:1;' +
      'opacity:0;pointer-events:none;transition:opacity .15s ease;}' +
      '.hit-carousel__dot{width:6px;height:6px;border-radius:50%;' +
      'background:rgba(255,255,255,.65);border:none;padding:0;cursor:pointer;}' +
      '.hit-carousel__dot.is-active{background:#fff;box-shadow:0 0 0 1px rgba(0,0,0,.25);}' +
      '.hit-wrapper:hover .hit-carousel__arrow,' +
      '.hit-wrapper:focus-within .hit-carousel__arrow,' +
      '.hit-wrapper:hover .hit-carousel__dots,' +
      '.hit-wrapper:focus-within .hit-carousel__dots{opacity:1;pointer-events:auto;}' +
      /* Mobile: dots only, always visible; no arrows — swipe replaces them. */
      '@media (max-width:749px){' +
      '.hit-carousel__arrow{display:none!important;}' +
      '.hit-carousel__dots{opacity:1!important;pointer-events:none;bottom:12px;}' +
      '}';
    document.head.appendChild(style);
  }

  function normalizeImageSrc(im) {
    if (typeof im === 'string') return im;
    return (im && im.src) || null;
  }

  function callGloInit() {
    if (typeof window.globoswatch !== 'undefined' && typeof window.globoswatch.init === 'function') {
      window.globoswatch.init();
    }
  }

  // GLO Color Swatch app config (snippets/globo.swatch.custom.liquid) tells GLO's
  // script which selectors match our search-result cards, but GLO's own script only
  // scans the DOM once on initial page load — it never sees cards Algolia re-renders
  // via AJAX (pagination, filter, sort, tab switch). Re-calling its init after each
  // render makes it rescan.
  function reinitGloSwatches() {
    callGloInit();
    // GLO fetches each card's variant/swatch data individually over the network as it
    // scans. On slower/higher-latency connections — seen specifically in the native
    // in-app WebView (webview-handler.js sets html.webview) — some of those requests can
    // still be in flight when the scan completes, leaving those cards without swatches.
    // Follow-up passes catch anything that was still loading. Desktop/mobile browser
    // already renders reliably on the first pass, so this is scoped to the WebView only
    // rather than adding needless repeat calls to GLO's backend everywhere.
    var isWebview = document.documentElement.classList.contains('webview');
    if (isWebview) {
      // Two distinct failure modes, two retries. A short one catches requests that were
      // simply still in flight when the scan completed (no rate limit involved). A single
      // longer-spaced one catches requests GLO's backend actually 429'd — it must land
      // after that rate-limit window has reset, since a retry inside the still-active
      // window just gets 429'd again too.
      var retryDelays = [3000, 35000];
      retryDelays.forEach(function (delay) {
        setTimeout(callGloInit, delay);
      });
    }
  }

  var WEBVIEW_GLO_BATCH_SIZE = 8;
  var WEBVIEW_GLO_BATCH_DELAY = 700;

  // GLO scans the whole DOM the instant init() runs and, per observed behavior,
  // checks colors for every matched card at once — a full page of cards rendered in
  // one shot means a burst of that many simultaneous requests against GLO's backend,
  // the suspected trigger for its rate limiting in the WebView. Revealing cards to
  // GLO in small batches instead keeps each of its scans smaller, spread over roughly
  // a second instead of one spike. WebView only — desktop/mobile browser render
  // everything immediately as before, since no rate-limit issue was observed there.
  function revealRemainingProductCards(container, gridSelector, remainingHits) {
    var grid = container.querySelector(gridSelector);
    if (!grid) return;

    function appendNextBatch() {
      var batch = remainingHits.splice(0, WEBVIEW_GLO_BATCH_SIZE);
      if (batch.length === 0) return;
      grid.insertAdjacentHTML('beforeend', batch.map(renderProductCard).join(''));
      callGloInit();
      enhanceCardsAfterGlo(container);
      if (remainingHits.length > 0) {
        setTimeout(appendNextBatch, WEBVIEW_GLO_BATCH_DELAY);
      }
    }
    setTimeout(appendNextBatch, WEBVIEW_GLO_BATCH_DELAY);
  }

  function enhanceCard(card) {
    var handle = card.getAttribute('data-product-handle');
    if (!handle) return;
    enhanceOptionHintsForCard(card, handle);
    enhanceImageCarouselForCard(card, handle);
  }

  // Both enhanceOptionHintsForCard and enhanceImageCarouselForCard fetch the same
  // /products/{handle}.js endpoint GLO's own swatch check hits. Firing that fetch for
  // every card on the page at once — 20-30 requests immediately, layered on top of
  // GLO's own per-product requests — is exactly the kind of simultaneous request burst
  // the WebView's smaller per-host connection pool struggles with more than a desktop
  // browser does. Deferring off-screen cards until they actually scroll into view keeps
  // the initial burst down to whatever's visible, rather than the whole page at once.
  function lazyEnhanceCards(container) {
    injectCarouselStyles();
    var cards = container.querySelectorAll('[data-product-handle]');
    if (!('IntersectionObserver' in window)) {
      cards.forEach(enhanceCard);
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          enhanceCard(entry.target);
        });
      },
      { rootMargin: '400px 0px' },
    );
    cards.forEach(function (card) {
      observer.observe(card);
    });
  }

  // In the WebView, a card GLO fails to fetch on its first scan never gets retried by
  // later globoswatch.init() calls, so the only other lever is reducing how many fail on
  // that first attempt — giving GLO's initial fetch burst a short head start before ours
  // joins the same connection pool. Not needed outside the WebView, where this
  // contention wasn't observed.
  function enhanceCardsAfterGlo(container) {
    if (document.documentElement.classList.contains('webview')) {
      setTimeout(function () {
        lazyEnhanceCards(container);
      }, 500);
    } else {
      lazyEnhanceCards(container);
    }
  }

  // Fetches one card's full image list from Shopify and, when there's more than
  // one image, upgrades its static image into a carousel with prev/next arrows
  // and dots. Called per-card by lazyEnhanceCards() above, once that card is
  // actually near the viewport.
  function enhanceImageCarouselForCard(card, handle) {
    var mediaEl = card.querySelector('.hit-image');
    if (!mediaEl || mediaEl.hasAttribute('data-carousel-ready')) return;

    fetchProductData(handle).then(function (data) {
      // Another render pass may have already handled this exact element.
      if (mediaEl.hasAttribute('data-carousel-ready')) return;
      mediaEl.setAttribute('data-carousel-ready', '1');

      var rawImages = data && Array.isArray(data.images) ? data.images : [];
      var images = rawImages.map(normalizeImageSrc).filter(Boolean).slice(0, CAROUSEL_MAX_IMAGES);
      if (images.length < 2) return;

      // Warm the browser cache so switching images feels instant.
      images.forEach(function (src) {
        var preload = new Image();
        preload.src = src;
      });

      mediaEl.setAttribute('data-carousel-images', JSON.stringify(images));
      mediaEl.setAttribute('data-carousel-index', '0');

      var arrowsHtml =
        '<button type="button" class="hit-carousel__arrow hit-carousel__arrow--prev" aria-label="Previous image">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>' +
        '</button>' +
        '<button type="button" class="hit-carousel__arrow hit-carousel__arrow--next" aria-label="Next image">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
        '</button>';

      var dotsHtml =
        '<div class="hit-carousel__dots">' +
        images
          .map(function (_, i) {
            return (
              '<button type="button" class="hit-carousel__dot' +
              (i === 0 ? ' is-active' : '') +
              '" data-carousel-dot="' +
              i +
              '" aria-label="Show image ' +
              (i + 1) +
              '"></button>'
            );
          })
          .join('') +
        '</div>';

      mediaEl.insertAdjacentHTML('beforeend', arrowsHtml + dotsHtml);
    });
  }

  function setCarouselIndex(mediaEl, index) {
    var raw = mediaEl.getAttribute('data-carousel-images');
    if (!raw) return;
    var images = JSON.parse(raw);
    var next = ((index % images.length) + images.length) % images.length;
    mediaEl.setAttribute('data-carousel-index', String(next));
    var img = mediaEl.querySelector('img');
    if (img) img.src = images[next];
    mediaEl.querySelectorAll('.hit-carousel__dot').forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === next);
    });
  }

  // ─── Algolia Insights: result clicks ────────────────────────────────
  //
  // Covers every card type, not just products: collections, recipes, blogs and
  // pages all report impressions, so leaving their clicks untracked would show
  // those indices at a 0% click-through rate — which reads as a relevance
  // problem rather than missing instrumentation.
  //
  // Product cards are a <div class="hit-wrapper"> containing links; content
  // cards are a bare <a class="hit">. The selector matches both.
  //
  // Position comes from the stored search record, not from a data attribute on
  // the card. A card's render index is relative to its page and, in the webview
  // batching path, to its splice() batch — neither is the absolute position
  // Algolia expects. Looking the objectID up in the recorded result set gives
  // the true position on every path.
  document.addEventListener('click', function (e) {
    if (!window.AlgoliaInsights || !e.target.closest) return;

    var wrapper = e.target.closest('[data-object-id]');
    if (!wrapper) return;
    // Carousel arrows and dots sit inside the card but aren't result clicks.
    if (e.target.closest('.hit-carousel__arrow, .hit-carousel__dot')) return;
    if (!e.target.closest('a, button')) return;

    var objectID = wrapper.getAttribute('data-object-id');
    if (!objectID) return;

    // findSearchFor returns the freshest record containing this objectID, so a
    // click can't be attributed to a parallel ALL-tab search that didn't
    // contain it. No record means no live search — leave it unattributed.
    var record = window.AlgoliaInsights.findSearchFor(objectID);
    if (!record) return;

    var position = window.AlgoliaInsights.getPositionOf(record, objectID);
    window.AlgoliaInsights.publishClick('instantsearch', record.indexName, objectID, position, record.queryID);
  });

  // Delegated (survives re-renders).
  document.addEventListener('click', function (e) {
    var arrow = e.target.closest('.hit-carousel__arrow');
    var dot = e.target.closest('.hit-carousel__dot');
    if (!arrow && !dot) return;
    e.preventDefault();
    e.stopPropagation();
    var mediaEl = (arrow || dot).closest('.hit-image');
    if (!mediaEl) return;
    var current = parseInt(mediaEl.getAttribute('data-carousel-index') || '0', 10);
    if (arrow) {
      var dir = arrow.classList.contains('hit-carousel__arrow--next') ? 1 : -1;
      setCarouselIndex(mediaEl, current + dir);
    } else {
      setCarouselIndex(mediaEl, parseInt(dot.getAttribute('data-carousel-dot'), 10));
    }
  });

  // ─── Swipe to advance (mobile) ─────────────────────────────────────
  // Arrows are hidden under 749px (see injectCarouselStyles above), so a
  // horizontal drag on the image is how mobile visitors move through the
  // photos instead — matching the dots-only reference UX. Delegated on
  // document, same as the click handler above, so it works on cards that
  // haven't loaded their carousel yet just as well as ones that have.
  var carouselSwipe = null;
  var CAROUSEL_SWIPE_THRESHOLD = 30;

  document.addEventListener(
    'touchstart',
    function (e) {
      var mediaEl = e.target.closest && e.target.closest('.hit-image[data-carousel-images]');
      if (!mediaEl) return;
      var t = e.touches[0];
      carouselSwipe = { mediaEl: mediaEl, startX: t.clientX, startY: t.clientY, horizontal: false };
    },
    { passive: true },
  );

  document.addEventListener(
    'touchmove',
    function (e) {
      if (!carouselSwipe) return;
      var t = e.touches[0];
      var dx = t.clientX - carouselSwipe.startX;
      var dy = t.clientY - carouselSwipe.startY;
      // Once a drag reveals itself as horizontal, remember that — avoids
      // flip-flopping mid-gesture and lets a mostly-vertical page scroll
      // pass through untouched.
      if (!carouselSwipe.horizontal && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        carouselSwipe.horizontal = true;
      }
    },
    { passive: true },
  );

  document.addEventListener('touchend', function (e) {
    if (!carouselSwipe) return;
    var swipe = carouselSwipe;
    carouselSwipe = null;
    if (!swipe.horizontal) return;
    var t = e.changedTouches[0];
    var dx = t.clientX - swipe.startX;
    if (Math.abs(dx) < CAROUSEL_SWIPE_THRESHOLD) return;
    var current = parseInt(swipe.mediaEl.getAttribute('data-carousel-index') || '0', 10);
    setCarouselIndex(swipe.mediaEl, current + (dx < 0 ? 1 : -1));
  });
  /* END -code for the product carousel dots and arrow for each products*/

  /**
   * Deduplicate product hits by id (Shopify product ID).
   * Each Algolia hit is a variant — multiple variants share the same id.
   * We keep only the first hit per product to show distinct products.
   * Also adjusts nbHits, nbPages, and pages to reflect the deduplicated count.
   */
  function deduplicateProducts(result) {
    if (!result || !result.hits) return result;

    var seen = {};
    var unique = [];
    result.hits.forEach(function (hit) {
      var productId = hit.id;
      if (!productId || !seen[productId]) {
        if (productId) seen[productId] = true;
        unique.push(hit);
      }
    });

    var dedupCount = unique.length;
    var originalCount = result.hits.length;
    var ratio = originalCount > 0 ? dedupCount / originalCount : 1;

    result.hits = unique;
    // Apply ratio to estimate total deduplicated count across all pages
    // Special case: if nbHits equals hits.length, use exact count (single page result)
    if (result.nbHits === originalCount) {
      // Single page or exact match - use actual deduplicated count
      result.nbHits = dedupCount;
    } else if (ratio !== 1) {
      // Multi-page results with deduplication - apply ratio to estimate total
      result.nbHits = Math.round(result.nbHits * ratio);
    }
    // else: ratio === 1 means no deduplication, keep original nb Hits unchanged
    result.nbPages = Math.ceil(result.nbHits / result.hitsPerPage);

    return result;
  }

  function formatDate(isoStr) {
    if (!isoStr) return '';
    var d = new Date(isoStr);
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
  }

  function stripHtml(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  }

  function getSortIndex(scope) {
    var sort = currentSort[scope || 'products'];
    if (sort === 'best_selling') return config.indices.productsBestSelling;
    if (sort === 'title_asc') return config.indices.productsAsc;
    if (sort === 'title_desc') return config.indices.productsDesc;
    if (sort === 'price_asc') return config.indices.productsPriceAsc;
    if (sort === 'price_desc') return config.indices.productsPriceDesc;
    if (sort === 'oldest') return config.indices.productsOldest;
    if (sort === 'newest') return config.indices.productsNewest;
    return config.indices.products;
  }

  // scope is 'recipes' or 'blogs' — each has its own sort state, but both
  // read from the same set of shopify_articles_* replicas since Recipes and
  // Blogs are both drawn from the articles index.

  function getArticleSortIndex(scope) {
    var sort = currentSort[scope];
    if (sort === 'title_asc') return config.indices.articlesTitleAsc;
    if (sort === 'title_desc') return config.indices.articlesTitleDesc;
    if (sort === 'newest') return config.indices.articlesPublishedAtDesc;
    if (sort === 'oldest') return config.indices.articlesPublishedAtAsc;
    return config.indices.articles;
  }

  // Fallback only, used if config.articleFacets is ever unset.
  var ARTICLE_FACET_ATTRS = ['blog.title', 'tags', 'author.name'];

  // Which activeFilters/currentSort bucket a tab reads/writes. ALL maps to
  // 'products' (shared); RECIPES/BLOGS keep their own.
  function getFilterScope(tabKey) {
    if (tabKey === 'recipes') return 'recipes';
    if (tabKey === 'blogs') return 'blogs';
    return 'products';
  }

  // Get analytics scope for search events (differs from filter scope)
  // ALL tab should report 'all' for analytics, not 'products'
  function getAnalyticsScope(tabKey) {
    if (tabKey === 'all') return 'all';
    if (tabKey === 'recipes') return 'recipes';
    if (tabKey === 'blogs') return 'blogs';
    if (tabKey === 'products') return 'products';
    if (tabKey === 'collections') return 'collections';
    if (tabKey === 'pages') return 'pages';
    return 'all'; // Default to 'all' for unknown tabs
  }

  // Same mapping for currentPage, but unlike getFilterScope must NOT
  // default COLLECTIONS/PAGES to 'products' — they have their own page
  // position despite having no filter UI.
  function getPageScope(tabKey) {
    return tabKey === 'all' ? 'products' : tabKey;
  }

  function buildFacetFiltersFor(attrs, filtersObj) {
    var filters = [];
    (attrs || []).forEach(function (attr) {
      if (filtersObj[attr] && filtersObj[attr].length > 0) {
        filters.push(
          filtersObj[attr].map(function (val) {
            // facetFilters entries split on the FIRST colon only — everything
            // after is the literal facet value, so it must be sent raw
            // (quotes here would become part of the value Algolia matches on,
            // breaking any value that itself contains a ", like dimensions).
            return attr + ':' + val;
          }),
        );
      }
    });
    return filters;
  }

  /* function buildFacetFilters() {
    // When config.facets requests everything ('*'), the set of attributes a
    // user can actually check is whatever came back in the last response and
    // got rendered as a checkbox — i.e. the keys already present in
    // activeFilters.products. Falling back to a hardcoded whitelist here
    // caused checkboxes for facets outside that list (e.g. meta.netsuite.color,
    // price) to update the UI but never reach the Algolia query.
    var attrs = config.facets && config.facets[0] !== '*' ? config.facets : Object.keys(activeFilters.products);
    return buildFacetFiltersFor(attrs, activeFilters.products);
  } */

  /* function buildArticleFacetFilters(scope) {
    return buildFacetFiltersFor(config.articleFacets || ARTICLE_FACET_ATTRS, activeFilters[scope] || {});
  } */

  // Runs a main (conjunctive-across-attributes) query plus one supplemental,
  // hits-free query per facet attribute that currently has a selection —
  // with that attribute's OWN filter excluded from the supplemental query —
  // then splices each supplemental query's facet counts back into the main
  // response. This is Algolia/Shopify Search & Discovery's standard
  // "disjunctive faceting": without it, a single conjunctive query can't
  // distinguish "narrow the other facet groups" (Color:Red narrowing Size/
  // Category — correct, kept) from "narrow this facet group's own remaining
  // options" (Red disappearing Blue/Green from the Color list — the bug).
  function loadWithDisjunctiveFacets(indexName, baseParams, requestedFacets, filterAttrs, scopedFilters) {
    var facetsWildcard = Array.isArray(requestedFacets) && requestedFacets.indexOf('*') !== -1;
    var mainFacetFilters = buildFacetFiltersFor(filterAttrs, scopedFilters);
    var mainParams = Object.assign({ query: currentQuery, facets: requestedFacets, clickAnalytics: true }, baseParams);
    if (mainFacetFilters.length > 0) mainParams.facetFilters = mainFacetFilters;

    var selfFilteredAttrs = filterAttrs.filter(function (attr) {
      return (
        scopedFilters[attr] &&
        scopedFilters[attr].length > 0 &&
        (facetsWildcard || requestedFacets.indexOf(attr) !== -1)
      );
    });

    var queries = [{ indexName: indexName, params: mainParams }];
    selfFilteredAttrs.forEach(function (attr) {
      var otherAttrs = filterAttrs.filter(function (a) {
        return a !== attr;
      });
      var facetFilters = buildFacetFiltersFor(otherAttrs, scopedFilters);
      var params = Object.assign({ query: currentQuery, facets: [attr] }, baseParams, {
        hitsPerPage: 0,
        page: 0,
      });
      if (facetFilters.length > 0) params.facetFilters = facetFilters;
      queries.push({ indexName: indexName, params: params });
    });

    return batchedSearch(queries)
      .then(function (res) {
        var results = res.results;
        var main = results[0];
        // Capture queryID for analytics attribution
        if (main.queryID) {
          captureQueryID(indexName, main.queryID);
        }
        selfFilteredAttrs.forEach(function (attr, i) {
          var extra = results[i + 1];
          if (extra && extra.facets && extra.facets[attr]) {
            main.facets = main.facets || {};
            main.facets[attr] = extra.facets[attr];
          }
        });
        return main;
      })
      .catch(function () {
        return emptyResult(baseParams);
      });
  }

  // ─── Tab definitions ────────────────────────────────────────────────

  var TABS = [
    { key: 'all', label: 'ALL' },
    { key: 'products', label: 'PRODUCTS' },
    { key: 'collections', label: 'COLLECTIONS' },
    { key: 'recipes', label: 'RECIPES' },
    { key: 'blogs', label: 'BLOGS' },
    { key: 'pages', label: 'PAGES' },
  ];

  // ─── API calls ──────────────────────────────────────────────────────

  // Billing is per request and one request holds up to 50 queries, so searches
  // fired in the same tick are collected and sent as one.
  var batchQueue = [];
  var batchScheduled = false;

  function batchedSearch(queries) {
    return new Promise(function (resolve, reject) {
      batchQueue.push({ queries: queries, resolve: resolve, reject: reject });
      if (batchScheduled) return;
      batchScheduled = true;
      Promise.resolve().then(flushBatch);
    });
  }

  function flushBatch() {
    var pending = batchQueue;
    batchQueue = [];
    batchScheduled = false;
    if (!pending.length) return;

    // Each entry remembers where its own queries start so its caller receives
    // exactly its own slice back, in the order it asked for them.
    var all = [];
    pending.forEach(function (entry) {
      entry.offset = all.length;
      all = all.concat(entry.queries);
    });

    searchClient
      .search(all)
      .then(function (res) {
        var results = (res && res.results) || [];
        pending.forEach(function (entry) {
          entry.resolve({ results: results.slice(entry.offset, entry.offset + entry.queries.length) });
        });
      })
      .catch(function (err) {
        pending.forEach(function (entry) {
          entry.reject(err);
        });
      });
  }

  // loadAllTabs gathers every tab load in one Promise.all, so a single rejection
  // takes the whole page render down. An empty result leaves that section empty
  // and the rest intact; hitsPerPage stays non-zero to keep nbPages off NaN.
  function emptyResult(params) {
    return {
      hits: [],
      nbHits: 0,
      nbPages: 0,
      page: (params && params.page) || 0,
      hitsPerPage: (params && params.hitsPerPage) || 1,
      facets: {},
      // Lets the products grid tell "we could not check" apart from "nothing
      // matched" — the secondary sections stay silent either way.
      searchFailed: true,
    };
  }

  function productsSearchFailed() {
    var data = tabData.allTabProducts || tabData.products;
    return !!(data && data.searchFailed);
  }

  function searchIndex(indexName, params) {
    return batchedSearch([
      {
        indexName: indexName,
        params: Object.assign({ query: currentQuery, clickAnalytics: true }, params || {}),
      },
    ])
      .then(function (res) {
        var result = res.results[0];
        // Capture queryID for analytics attribution
        if (result.queryID) {
          captureQueryID(indexName, result.queryID);
        }
        return result;
      })
      .catch(function () {
        return emptyResult(params);
      });
  }

  // Smallest number evenly divisible by every column count passed in.
  // Used below to round a configured hits-per-page value up so a full
  // page's last row is never left half- (or single-) filled at ANY of a
  // grid's responsive column counts, not just its widest one.
  function greatestCommonDivisor(a, b) {
    return b === 0 ? a : greatestCommonDivisor(b, a % b);
  }
  function leastCommonMultiple(numbers) {
    return numbers.reduce(function (acc, n) {
      return (acc * n) / greatestCommonDivisor(acc, n);
    }, 1);
  }

  // The products grid renders 2-across on mobile, 3-across while the
  // facet sidebar/drawer is showing on tablet/desktop, and 5-across once
  // it's hidden (see `.algolia-product-grid` vs
  // `.algolia-product-grid--no-facets` at each breakpoint in the CSS,
  // driven by hasFacetsToShow()) — but whether this query actually has
  // facets to show is only known once its own response comes back, so
  // hitsPerPage can't pick which column count to round to ahead of the
  // request that determines it. Rounding config.productsPerPage UP to the
  // nearest multiple of 30 (LCM of 2, 3, and 5) guarantees a full last row
  // at every one of those column counts: e.g. a configured 12 becomes 30,
  // which divides evenly into rows of 2, 3, AND 5 — so switching between
  // the mobile, sidebar, and no-facets layouts never leaves a trailing
  // row like 2, 2, 2, 2, 2, 2, 2, 1. Rounding to just 15 (3 × 5) fixed the
  // 3-vs-5 case but missed mobile's 2 columns, which is what produced
  // that exact 8-rows-of-2-then-1 pattern. Same "may request a few more
  // hits, never fewer" approach as getFullWidthGridHitsPerPage() below.
  var PRODUCT_GRID_COLUMN_COUNTS = [2, 3, 5];
  function getProductsGridHitsPerPage() {
    var configured = config.productsPerPage || 0;
    var lcm = leastCommonMultiple(PRODUCT_GRID_COLUMN_COUNTS); // 30
    var rounded = Math.ceil(configured / lcm) * lcm;
    return rounded > 0 ? rounded : configured;
  }

  function loadProducts(page, scope) {
    var indexName = getSortIndex(scope);
    var scopedFilters = activeFilters[scope];
    var requestedFacets = config.facets || [
      'product_type',
      'options.color',
      'options.size',
      'options.fabric',
      'price_range',
    ];
    // When config.facets requests everything ('*'), the set of attributes a
    // user can actually check is whatever came back in the last response and
    // got rendered as a checkbox — i.e. the keys already present in
    // scopedFilters. Falling back to a hardcoded whitelist here caused
    // checkboxes for facets outside that list (e.g. meta.netsuite.color,
    // price) to update the UI but never reach the Algolia query.
    var filterAttrs = config.facets && config.facets[0] !== '*' ? config.facets : Object.keys(scopedFilters);
    var baseParams = {
      hitsPerPage: getProductsGridHitsPerPage(),
      page: page || 0,
      attributesToHighlight: ['title'],
      distinct: 1,
      facetingAfterDistinct: true,
      maxValuesPerFacet: config.maxValuesPerFacet || 100,
      clickAnalytics: true,
    };
    return loadWithDisjunctiveFacets(indexName, baseParams, requestedFacets, filterAttrs, scopedFilters);
  }

  // ─── searchResults Event Publishing ───────────────────

  /**
   * Publish searchResults analytics event
   * @param {string} scope - Filter scope ('all', 'products', 'recipes', 'blogs')
   */
  function publishSearchResultsEvent(scope) {
    if (!window.Shopify || !window.Shopify.analytics) return;

    scope = scope || 'all';

    // Calculate result count based on scope
    var totalResults = 0;

    if (scope === 'all') {
      // 'all' scope: report combined count across all content types
      totalResults += (tabData.allTabProducts && tabData.allTabProducts.nbHits) || 0;
      totalResults += (tabData.allTabCollections && tabData.allTabCollections.nbHits) || 0;
      totalResults += (tabData.allTabRecipes && tabData.allTabRecipes.nbHits) || 0;
      totalResults += (tabData.allTabBlogs && tabData.allTabBlogs.nbHits) || 0;
      totalResults += (tabData.allTabPages && tabData.allTabPages.nbHits) || 0;
    } else if (scope === 'products') {
      // 'products' scope: report only products count
      totalResults = (tabData.allTabProducts && tabData.allTabProducts.nbHits) || 0;
    } else if (scope === 'recipes') {
      // 'recipes' scope: report only recipes count
      totalResults = (tabData.recipes && tabData.recipes.nbHits) || 0;
    } else if (scope === 'blogs') {
      // 'blogs' scope: report only blogs count
      totalResults = (tabData.blogs && tabData.blogs.nbHits) || 0;
    } else if (scope === 'collections') {
      // 'collections' scope: report only collections count
      totalResults = (tabData.collections && tabData.collections.nbHits) || 0;
    } else if (scope === 'pages') {
      // 'pages' scope: report only pages count
      totalResults = (tabData.pages && tabData.pages.nbHits) || 0;
    } else {
      // Unknown scope: fallback to combined count
      totalResults += (tabData.allTabProducts && tabData.allTabProducts.nbHits) || 0;
      totalResults += (tabData.allTabCollections && tabData.allTabCollections.nbHits) || 0;
      totalResults += (tabData.allTabRecipes && tabData.allTabRecipes.nbHits) || 0;
      totalResults += (tabData.allTabBlogs && tabData.allTabBlogs.nbHits) || 0;
      totalResults += (tabData.allTabPages && tabData.allTabPages.nbHits) || 0;
    }

    // Build filterTerm array from active filters for the correct scope
    var filterTermArray = [];
    var scopedFilters = activeFilters[scope] || {};
    Object.keys(scopedFilters).forEach(function (facetName) {
      var values = scopedFilters[facetName];
      if (Array.isArray(values)) {
        values.forEach(function (value) {
          filterTermArray.push(facetName.toLowerCase() + ':' + String(value).toLowerCase());
        });
      }
    });

    // Publish searchResults event
    window.Shopify.analytics.publish('search-event', {
      term: currentQuery,
      resultsCount: totalResults,
      filters: filterTermArray,
    });
  }

  /**
   * Publish click analytics event (Path A: direct publish)
   * @param {string} moduleName - Analytics module name
   * @param {string} linkTitle - Link title
   * @param {string} linkText - Link text token
   */
  function publishClickEvent(moduleName, linkTitle, linkText) {
    if (!window.Shopify || !window.Shopify.analytics) return;

    var clickData = {
      moduleName: moduleName || '',
      linkTitle: linkTitle || '',
      linkText: linkText || '',
      targetURL: null,
      locURL: window.location.href,
    };

    try {
      window.Shopify.analytics.publish('page-click', { clickData: clickData });
    } catch (error) {
      console.error('[Algolia Analytics] Failed to publish click event:', error);
    }
  }

  // Collections/Recipes/Blogs/Pages render 2 columns per row on mobile
  // and up to 5 columns per row on desktop (Collections/Pages always;
  // Recipes/Blogs once the sidebar is hidden — see the `--no-facets` grid
  // rules in the CSS). All four share a single `config.otherTabsPerPage`
  // value that isn't guaranteed to be a multiple of every column count it
  // renders at, so a full page of hits can end with a half- (or
  // single-) filled last row — e.g. 12 hits rounded to 15 fills desktop's
  // 5-across rows perfectly (5, 5, 5), but 15 is odd, so mobile's
  // 2-across layout turns that into 2, 2, 2, 2, 2, 2, 2, 1: eight rows
  // with a lone card on the last one. Rounding UP to the nearest multiple
  // of 10 (LCM of 2 and 5) instead means every full page completes its
  // last row cleanly at both column counts — it may request a few more
  // hits per page than configured, never fewer, so no row is ever left
  // partially filled.
  var FULL_WIDTH_GRID_COLUMN_COUNTS = [2, 5];
  function getFullWidthGridHitsPerPage() {
    var configured = config.otherTabsPerPage || 0;
    var lcm = leastCommonMultiple(FULL_WIDTH_GRID_COLUMN_COUNTS); // 10
    var rounded = Math.ceil(configured / lcm) * lcm;
    return rounded > 0 ? rounded : configured;
  }

  function loadTabData(tabKey, page) {
    var p = page || 0;
    if (tabKey === 'all') {
      // Same query as PRODUCTS (shared state) — reuse it.
      return loadTabData('products', p);
    }
    if (tabKey === 'products') {
      return loadProducts(p, 'products').then(function (res) {
        tabData.products = deduplicateProducts(res);
        tabData.allTabProducts = tabData.products;
        // Recorded here rather than inside the search call: deduplicateProducts
        // mutates result.hits, so recording upstream would store the pre-dedup
        // list while the grid renders the post-dedup one, shifting every
        // position after the first duplicate.
        if (window.AlgoliaInsights) {
          window.AlgoliaInsights.recordSearch(
            getSortIndex('products'),
            tabData.products,
            p,
            tabData.products.hitsPerPage,
          );
        }
        // Do NOT publish here - will be published after all tabs load
        return tabData.products;
      });
    }
    if (tabKey === 'collections') {
      return searchIndex(config.indices.collections, {
        hitsPerPage: getFullWidthGridHitsPerPage(),
        page: p,
        facets: [],
      }).then(function (res) {
        tabData.collections = res;
        if (window.AlgoliaInsights) {
          window.AlgoliaInsights.recordSearch(config.indices.collections, res, p, res.hitsPerPage, 'collections');
        }
        return res;
      });
    }
    if (tabKey === 'recipes') {
      var recipeFacets = config.articleFacets || ARTICLE_FACET_ATTRS;
      // Same wildcard handling as loadProducts: when requesting every facet,
      // disjunctive re-querying can only re-filter attributes the user has
      // actually checked (scopedFilters keys), not the literal '*' string.
      var recipeFilterAttrs = recipeFacets[0] !== '*' ? recipeFacets : Object.keys(activeFilters.recipes);
      return loadWithDisjunctiveFacets(
        getArticleSortIndex('recipes'),
        {
          hitsPerPage: getFullWidthGridHitsPerPage(),
          page: p,
          filters: 'blog.handle:"recipe"',
          maxValuesPerFacet: config.maxValuesPerFacet || 100,
          // The title/date-sorted replicas inherit index-level Distinct
          // settings from the primary index; force it off here so a sorted
          // query returns every matching article instead of one per group.
          distinct: 0,
        },
        recipeFacets,
        recipeFilterAttrs,
        activeFilters.recipes,
      ).then(function (res) {
        tabData.recipes = res;
        if (window.AlgoliaInsights) {
          window.AlgoliaInsights.recordSearch(getArticleSortIndex('recipes'), res, p, res.hitsPerPage, 'recipes');
        }
        // Do NOT publish here - will be published after all tabs load
        return res;
      });
    }
    if (tabKey === 'blogs') {
      var blogFacets = config.articleFacets || ARTICLE_FACET_ATTRS;
      var blogFilterAttrs = blogFacets[0] !== '*' ? blogFacets : Object.keys(activeFilters.blogs);
      return loadWithDisjunctiveFacets(
        getArticleSortIndex('blogs'),
        {
          hitsPerPage: getFullWidthGridHitsPerPage(),
          page: p,
          filters: 'NOT blog.handle:"recipe"',
          maxValuesPerFacet: config.maxValuesPerFacet || 100,
          distinct: 0,
        },
        blogFacets,
        blogFilterAttrs,
        activeFilters.blogs,
      ).then(function (res) {
        tabData.blogs = res;
        if (window.AlgoliaInsights) {
          window.AlgoliaInsights.recordSearch(getArticleSortIndex('blogs'), res, p, res.hitsPerPage, 'blogs');
        }
        // Do NOT publish here - will be published after all tabs load
        return res;
      });
    }
    if (tabKey === 'pages') {
      return searchIndex(config.indices.pages, {
        hitsPerPage: getFullWidthGridHitsPerPage(),
        page: p,
        facets: [],
      }).then(function (res) {
        tabData.pages = res;
        if (window.AlgoliaInsights) {
          window.AlgoliaInsights.recordSearch(config.indices.pages, res, p, res.hitsPerPage, 'pages');
        }
        return res;
      });
    }
    return Promise.resolve(null);
  }

  function loadAllTabs() {
    var productsBaseline = startProductsBaselineQuery();
    // Named so the previews can await a tab's result instead of re-querying it.
    // Still started in this tick, so a preview that does need its own query
    // joins the same batch rather than forming a second round trip.
    var tabs = {
      // No 'all' entry — populated as a side effect of the 'products' load.
      products: loadTabData('products', currentPage.products),
      collections: loadTabData('collections', currentPage.collections),
      recipes: loadTabData('recipes', currentPage.recipes),
      blogs: loadTabData('blogs', currentPage.blogs),
      pages: loadTabData('pages', currentPage.pages),
    };
    return Promise.all([
      tabs.products,
      tabs.collections,
      tabs.recipes,
      tabs.blogs,
      tabs.pages,
      loadAllTabPreviews(tabs),
    ]).then(function () {
      return loadBaselineCounts(productsBaseline);
    });
  }

  // ALL's Collections/Recipes/Blogs/Pages preview rows must stay unfiltered
  // even when the matching standalone tab is filtered/sorted/paginated, so
  // they get their own cache here (no filters, default sort, page 0),
  // fetched once. PRODUCTS is excluded — ALL intentionally shares its state.
  var allTabPreviewsLoaded = false;
  // A preview row and its tab ask for exactly the same thing while that tab sits
  // at page 0, unfiltered and unsorted. Sorting matters most: a non-default sort
  // resolves to a different index, so its result is not interchangeable.
  function isAtDefaults(scope) {
    if (currentPage[scope] !== 0) return false;
    if (!currentSort[scope]) return true;
    if (currentSort[scope] !== 'default') return false;
    return Object.keys(activeFilters[scope] || {}).length === 0;
  }

  function previewFor(scope, tabPromise, fetchPreview) {
    return isAtDefaults(scope) ? tabPromise : fetchPreview();
  }

  function loadAllTabPreviews(tabs) {
    if (allTabPreviewsLoaded) return Promise.resolve();
    allTabPreviewsLoaded = true;
    return Promise.all([
      previewFor('collections', tabs.collections, function () {
        return searchIndex(config.indices.collections, {
          hitsPerPage: getFullWidthGridHitsPerPage(),
          page: 0,
          facets: [],
        });
      }).then(function (res) {
        tabData.allTabCollections = res;
      }),
      previewFor('recipes', tabs.recipes, function () {
        return searchIndex(config.indices.articles, {
          hitsPerPage: getFullWidthGridHitsPerPage(),
          page: 0,
          filters: 'blog.handle:"recipe"',
          facets: [],
          distinct: 0,
        });
      }).then(function (res) {
        tabData.allTabRecipes = res;
      }),
      previewFor('blogs', tabs.blogs, function () {
        return searchIndex(config.indices.articles, {
          hitsPerPage: getFullWidthGridHitsPerPage(),
          page: 0,
          filters: 'NOT blog.handle:"recipe"',
          facets: [],
          distinct: 0,
        });
      }).then(function (res) {
        tabData.allTabBlogs = res;
      }),
      previewFor('pages', tabs.pages, function () {
        return searchIndex(config.indices.pages, {
          hitsPerPage: getFullWidthGridHitsPerPage(),
          page: 0,
          facets: [],
        });
      }).then(function (res) {
        tabData.allTabPages = res;
      }),
    ]);
  }

  // Started alongside the tab loads, not after, so it joins their batch rather
  // than forming a second request. Only needed when a filter hides the total.
  function startProductsBaselineQuery() {
    if (Object.keys(activeFilters.products).length === 0) return null;
    return searchIndex(config.indices.products, {
      hitsPerPage: getProductsGridHitsPerPage(),
      page: 0,
      distinct: 1,
    });
  }

  // Zero-filter totals for the static tab-count labels. COLLECTIONS/RECIPES/
  // BLOGS/PAGES read off the preview data above; PRODUCTS needs its own
  // zero-filter query when a deep link arrived with a filter already active.
  function loadBaselineCounts(productsBaseline) {
    baselineCounts.collections = tabData.allTabCollections ? tabData.allTabCollections.nbHits : 0;
    baselineCounts.recipes = tabData.allTabRecipes ? tabData.allTabRecipes.nbHits : 0;
    baselineCounts.blogs = tabData.allTabBlogs ? tabData.allTabBlogs.nbHits : 0;
    baselineCounts.pages = tabData.allTabPages ? tabData.allTabPages.nbHits : 0;

    if (!productsBaseline) {
      baselineCounts.products = tabData.products ? tabData.products.nbHits : 0;
      return Promise.resolve();
    }
    return productsBaseline.then(function (res) {
      baselineCounts.products = deduplicateProducts(res).nbHits;
    });
  }

  // ─── Render: Tabs ───────────────────────────────────────────────────

  function renderTabs() {
    var container = document.getElementById('algolia-tabs');
    if (!container) return;

    var html = '';
    TABS.forEach(function (tab) {
      if (!isTabVisible(tab.key)) return;
      var count = getTabCount(tab.key);
      var active = tab.key === currentTab ? ' algolia-tab--active' : '';
      html +=
        '<button class="algolia-tab' +
        active +
        '" data-tab="' +
        tab.key +
        '">' +
        tab.label +
        ' (' +
        count +
        ')' +
        '</button>';
    });
    container.innerHTML = html;

    container.querySelectorAll('.algolia-tab').forEach(function (btn) {
      btn.addEventListener('click', function () {
        switchTab(btn.getAttribute('data-tab'), 'desktop');
      });
    });
  }

  function renderMobileTabSelector() {
    var container = document.getElementById('algolia-mobile-tab-selector');
    if (!container) return;

    var visibleTabs = TABS.filter(function (tab) {
      return isTabVisible(tab.key);
    });

    // No tabs visible (true zero-results-anywhere query) — an empty <select>
    // still renders as an empty dropdown box with browser chrome, so hide
    // the whole container instead of leaving that behind.
    if (visibleTabs.length === 0) {
      container.innerHTML = '';
      container.style.display = 'none';
      return;
    }
    container.style.display = '';

    var html = '<select class="algolia-mobile-tab-select">';
    visibleTabs.forEach(function (tab) {
      var count = getTabCount(tab.key);
      var selected = tab.key === currentTab ? ' selected' : '';
      html += '<option value="' + tab.key + '"' + selected + '>' + tab.label + ' (' + count + ')' + '</option>';
    });
    html += '</select>';
    container.innerHTML = html;

    container.querySelector('select').addEventListener('change', function (e) {
      switchTab(e.target.value, 'mobile');
    });
  }

  // Tab labels always show the unfiltered baseline total, never a live count.
  function getTabCount(tabKey) {
    if (tabKey === 'all') {
      return (
        baselineCounts.products +
        baselineCounts.collections +
        baselineCounts.recipes +
        baselineCounts.blogs +
        baselineCounts.pages
      );
    }
    return baselineCounts[tabKey] || 0;
  }

  function isTabVisible(tabKey) {
    if (tabKey === 'products') return baselineCounts.products > 0;
    if (tabKey === 'all') {
      // ALL tab is visible only when non-product content has results
      return baselineCounts.collections + baselineCounts.recipes + baselineCounts.blogs + baselineCounts.pages > 0;
    }
    return getTabCount(tabKey) > 0;
  }

  // ─── Algolia Insights: impressions ──────────────────────────────────
  //
  // Every index a tab renders reports its own view event, not products alone —
  // the baseline did the same, and narrowing to products would drop visible
  // impression volume in the dashboard at cutover.
  //
  // Recipes and Blogs are two filtered views onto the same articles index, so
  // both resolve through getArticleSortIndex.
  function indicesRenderedBy(tabKey) {
    if (tabKey === 'all') {
      // Recipes and blogs carry independent sort state, so they can resolve to
      // different article replicas. Both are listed; when they resolve to the
      // same index the per-index guard collapses them to one event.
      return [
        getSortIndex('products'),
        config.indices.collections,
        getArticleSortIndex('recipes'),
        getArticleSortIndex('blogs'),
        config.indices.pages,
      ];
    }
    if (tabKey === 'products') return [getSortIndex('products')];
    if (tabKey === 'collections') return [config.indices.collections];
    if (tabKey === 'recipes') return [getArticleSortIndex('recipes')];
    if (tabKey === 'blogs') return [getArticleSortIndex('blogs')];
    if (tabKey === 'pages') return [config.indices.pages];
    return [];
  }

  function publishImpressions() {
    if (!window.AlgoliaInsights) return;

    indicesRenderedBy(currentTab).forEach(function (indexName) {
      if (!indexName) return;

      var record = window.AlgoliaInsights.getSearchFor(indexName);
      if (!record) return;

      // Guard is keyed by index — a single shared one would let the first
      // index reported swallow the other three on the ALL tab.
      if (lastImpressionQueryID[indexName] === record.queryID) return;
      lastImpressionQueryID[indexName] = record.queryID;

      if (record.objectIDs.length === 0) {
        // Insights has no zero-result method — Algolia derives those from the
        // search itself. Published on the existing bus for CJA only.
        if (window.Shopify && window.Shopify.analytics) {
          window.Shopify.analytics.publish('algolia_zero_results', {
            query: currentQuery,
            index: indexName,
          });
        }
        return;
      }

      window.AlgoliaInsights.publishView('instantsearch', indexName, record.objectIDs);
    });
  }

  function switchTab(tabKey, source) {
    currentTab = tabKey;
    persistUrlState(true);
    renderTabs();
    renderMobileTabSelector();
    renderSidebar();
    renderMobileFilterButton();
    renderCurrentTab();
    renderCurrentRefinements();

    // Publish search analytics event for new tab view
    var analyticsScope = getAnalyticsScope(tabKey);
    publishSearchResultsEvent(analyticsScope);

    publishImpressions();

    // Publish tab click analytics (B2/B3)
    // Desktop tabs = Path B (page reload), Mobile dropdown = Path A (client-side, no reload)
    if (source && source !== 'viewall' && window.Shopify && window.Shopify.analytics) {
      var tabLabel = TABS.find(function (t) {
        return t.key === tabKey;
      });
      if (tabLabel) {
        var linkText = source === 'mobile' ? 'tabSelect' : 'link';
        publishClickEvent(
          'searchResultsTabs',
          tabLabel.label,
          linkText,
          source === 'desktop' ? window.location.href : null,
        );
      }
    }
  }

  // ─── Render: Search title ───────────────────────────────────────────

  function updateSearchTitle(q) {
    var titleEl = document.querySelector('.custom-search-title');
    if (titleEl && q) {
      titleEl.textContent = 'Search results for "' + q + '"';
    }
  }

  // ─── Render: Current tab content ────────────────────────────────────

  function renderCurrentTab() {
    var container = document.getElementById('algolia-hits-container');
    if (!container) return;

    var layout = document.querySelector('.algolia-layout');
    if (layout) {
      layout.classList.add('algolia-layout--all-tab');
    }

    switch (currentTab) {
      case 'all':
        renderAllTab(container);
        break;
      case 'products':
        renderProductsTab(container);
        break;
      case 'collections':
        renderCollectionsTab(container);
        break;
      case 'recipes':
        renderRecipesTab(container);
        break;
      case 'blogs':
        renderBlogsTab(container);
        break;
      case 'pages':
        renderPagesTab(container);
        break;
    }

    // Update page title with current result count
    updatePageTitle();
  }

  /**
   * Update browser page title with current search results count
   * @returns {string} The new page title
   */
  function updatePageTitle() {
    if (!currentQuery) return document.title;

    var count = 0;
    var label = '';

    switch (currentTab) {
      case 'all':
        // For ALL tab: sum current filtered counts from each tab's data
        count = (tabData.allTabProducts && tabData.allTabProducts.nbHits) || 0;
        count += (tabData.allTabCollections && tabData.allTabCollections.nbHits) || 0;
        count += (tabData.allTabRecipes && tabData.allTabRecipes.nbHits) || 0;
        count += (tabData.allTabBlogs && tabData.allTabBlogs.nbHits) || 0;
        count += (tabData.allTabPages && tabData.allTabPages.nbHits) || 0;
        label = 'results';
        break;
      case 'products':
        count = (tabData.products && tabData.products.nbHits) || 0;
        label = 'products';
        break;
      case 'recipes':
        count = (tabData.recipes && tabData.recipes.nbHits) || 0;
        label = 'recipes';
        break;
      case 'blogs':
        count = (tabData.blogs && tabData.blogs.nbHits) || 0;
        label = 'blogs';
        break;
      case 'collections':
        count = (tabData.collections && tabData.collections.nbHits) || 0;
        label = 'collections';
        break;
      case 'pages':
        count = (tabData.pages && tabData.pages.nbHits) || 0;
        label = 'pages';
        break;
      default:
        count = (tabData.allTabProducts && tabData.allTabProducts.nbHits) || 0;
        label = 'results';
    }

    var newTitle = 'Search: ' + count + ' ' + label + ' found for "' + currentQuery + '" – Magnolia Dev Shop';
    document.title = newTitle;
    return newTitle;
  }

  // ─── Render: ALL tab ────────────────────────────────────────────────

  // ─── Render: Mobile section carousel nav (Blogs/Recipes preview rows) ──
  // Mobile-only prev/next control under a horizontally-scrolling row of
  // cards: a row of page dots on the left (first one shown as a wider
  // "active" dash) plus prev/next arrow buttons on the right. The dot count
  // is a placeholder at render time (bindSectionCarousels rebuilds it once
  // it knows the row's actual scrollable width) since how many cards fit
  // per "page" depends on the viewport, not the raw hit count.
  function renderCarouselNav(key, itemCount) {
    var prevArrow =
      '<svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true">' +
      '<path d="M9 2L4 7l5 5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var nextArrow =
      '<svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true">' +
      '<path d="M5 2l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    return (
      '<div class="algolia-carousel-nav" data-carousel-nav="' +
      key +
      '" data-item-count="' +
      itemCount +
      '">' +
      '<span class="algolia-carousel-nav__dots" data-carousel-dots></span>' +
      '<span class="algolia-carousel-nav__arrows">' +
      '<button type="button" class="algolia-carousel-nav__arrow algolia-carousel-nav__arrow--prev" aria-label="Previous" disabled>' +
      prevArrow +
      '</button>' +
      '<button type="button" class="algolia-carousel-nav__arrow algolia-carousel-nav__arrow--next" aria-label="Next">' +
      nextArrow +
      '</button>' +
      '</span>' +
      '</div>'
    );
  }

  // Binds every [data-carousel-nav] control in `container` to its matching
  // [data-carousel-track="<key>"] scroll row. Scoped to `container` (not a
  // global id lookup) so it works no matter how many times this renders and
  // never collides with anything else on the page.
  function bindSectionCarousels(container) {
    container.querySelectorAll('[data-carousel-nav]').forEach(function (nav) {
      var key = nav.getAttribute('data-carousel-nav');
      var wrap = nav.closest('.algolia-section__scroll-wrap');
      var track = wrap ? wrap.querySelector('[data-carousel-track="' + key + '"]') : null;
      if (!track) return;

      var prevBtn = nav.querySelector('.algolia-carousel-nav__arrow--prev');
      var nextBtn = nav.querySelector('.algolia-carousel-nav__arrow--next');
      var dotsEl = nav.querySelector('[data-carousel-dots]');
      var pageCount = 1;

      function buildDots() {
        var max = track.scrollWidth - track.clientWidth;
        if (max <= 4 || !track.clientWidth) {
          nav.style.display = 'none';
          return;
        }
        nav.style.display = '';
        pageCount = Math.max(2, Math.round(track.scrollWidth / track.clientWidth));
        var dotsHtml = '';
        for (var i = 0; i < pageCount; i++) {
          dotsHtml += '<span class="algolia-carousel-nav__dot"></span>';
        }
        dotsEl.innerHTML = dotsHtml;
      }

      function update() {
        var max = track.scrollWidth - track.clientWidth;
        if (max <= 4) return;
        var progress = track.scrollLeft / max;
        var activeIndex = Math.min(pageCount - 1, Math.round(progress * (pageCount - 1)));
        var dots = dotsEl.querySelectorAll('.algolia-carousel-nav__dot');
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === activeIndex);
        });
        prevBtn.disabled = track.scrollLeft <= 4;
        nextBtn.disabled = track.scrollLeft >= max - 4;
      }

      function scrollByPage(dir) {
        track.scrollBy({ left: dir * track.clientWidth * 0.92, behavior: 'smooth' });
      }

      prevBtn.addEventListener('click', function () {
        scrollByPage(-1);
      });
      nextBtn.addEventListener('click', function () {
        scrollByPage(1);
      });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', function () {
        buildDots();
        update();
      });

      buildDots();
      update();
    });
  }

  function renderAllTab(container) {
    var data = tabData.allTabProducts || tabData.products;
    var html = '';

    var hasAnyResults =
      (data && data.hits && data.hits.length > 0) ||
      (tabData.allTabCollections && tabData.allTabCollections.hits && tabData.allTabCollections.hits.length > 0) ||
      (tabData.allTabBlogs && tabData.allTabBlogs.hits && tabData.allTabBlogs.hits.length > 0) ||
      (tabData.allTabRecipes && tabData.allTabRecipes.hits && tabData.allTabRecipes.hits.length > 0) ||
      (tabData.allTabPages && tabData.allTabPages.hits && tabData.allTabPages.hits.length > 0);

    var needsDividerBeforeNextSection = false;

    if (productsSearchFailed()) {
      html += renderSearchError();
    } else if (!hasAnyResults) {
      html += renderEmptyState();
    } else if (data && baselineCounts.products === 0) {
      html += renderNoProductsFound();
    } else if (data) {
      needsDividerBeforeNextSection = true;

      // 'products' scope below — ALL shares PRODUCTS' state.
      var allFacetsHtml = buildSidebarHtml('products');
      var allHasFacets = hasFacetsToShow(allFacetsHtml);

      html +=
        '<div class="algolia-all-products-section' +
        (allHasFacets ? '' : ' algolia-all-products-section--no-facets') +
        '">';

      if (allHasFacets) {
        html += '<div class="algolia-all-sidebar">';
        html += buildCurrentRefinementsHtml('products');
        html += allFacetsHtml;
        html += '</div>';
      }

      html += '<div class="algolia-all-products-main">';
      html += '<div class="algolia-results-header">';
      html += '<div class="algolia-results-header__left">';
      html += '<div class="algolia-search-result">Search results for <b>"' + currentQuery + '"</b></div>';
      html += '<div class="algolia-results-stats">' + data.nbHits + ' products found</div>';
      html +=
        '<div class="algolia-mobile-filter-btn">' +
        buildMobileFilterButtonHtml() +
        buildMobileCurrentRefinementsHtml('products') +
        '</div>';
      html += '</div>';
      html += renderSortDropdown('products');
      html += '</div>';
      /* html += renderMobileFilterButton(); */
      if (data.hits && data.hits.length > 0) {
        html += '<div class="algolia-product-grid' + (allHasFacets ? '' : ' algolia-product-grid--no-facets') + '">';
        var allTabIsWebview = document.documentElement.classList.contains('webview');
        var allTabInitialHits = allTabIsWebview ? data.hits.slice(0, WEBVIEW_GLO_BATCH_SIZE) : data.hits;
        allTabInitialHits.forEach(function (hit) {
          html += renderProductCard(hit);
        });
        html += '</div>';
        html += renderPagination(data.page, data.nbPages);
      } else {
        html += renderNoFilteredResults('products');
      }
      html += '</div>';

      html += '</div>';
    }

    // Collections section — full width
    var collData = tabData.allTabCollections;
    if (collData && collData.hits && collData.hits.length > 0) {
      if (needsDividerBeforeNextSection) html += '<div class="algolia-divider"></div>';
      html += needsDividerBeforeNextSection
        ? '<div class="algolia-section">'
        : '<div class="algolia-section algolia-section--no-border-top">';
      needsDividerBeforeNextSection = true;
      html += '<div class="algolia-section__header">';
      html += '<span class="algolia-section__title">Collections</span>';
      html +=
        '<button type="button" class="algolia-section__viewall" data-switch-tab="collections" data-analytics-module="searchResultsSection:collection" data-analytics-link-title="viewAllCollections" data-link-text="link">VIEW ALL</button>';
      html += '</div>';
      html += '<div class="algolia-collection-grid">';
      collData.hits.slice(0, 6).forEach(function (hit) {
        html += renderCollectionCard(hit);
      });
      html += '</div>';
      html += '</div>';
    }

    // Recipes section — full width
    var recData = tabData.allTabRecipes;
    if (recData && recData.hits && recData.hits.length > 0) {
      var recHits = recData.hits.slice(0, 5);
      if (needsDividerBeforeNextSection) html += '<div class="algolia-divider"></div>';
      html += needsDividerBeforeNextSection
        ? '<div class="algolia-section">'
        : '<div class="algolia-section algolia-section--no-border-top">';
      needsDividerBeforeNextSection = true;
      html += '<div class="algolia-section__header">';
      html += '<span class="algolia-section__title">Recipes</span>';
      html +=
        '<button type="button" class="algolia-section__viewall" data-switch-tab="recipes" data-analytics-module="searchResultsSection:recipe" data-analytics-link-title="viewAllRecipes" data-link-text="link">VIEW ALL</button>';
      html += '</div>';
      html += '<div class="algolia-section__scroll-wrap">';
      html += '<div class="algolia-article-grid" data-carousel-track="recipes">';
      recHits.forEach(function (hit) {
        html += renderArticleCard(hit, 'recipe');
      });
      html += '</div>';
      html += renderCarouselNav('recipes', recHits.length);
      html += '</div>';
      html += '</div>';
    }

    // Blogs section — full width
    var blogData = tabData.allTabBlogs;
    if (blogData && blogData.hits && blogData.hits.length > 0) {
      var blogHits = blogData.hits.slice(0, 5);
      if (needsDividerBeforeNextSection) html += '<div class="algolia-divider"></div>';
      html += needsDividerBeforeNextSection
        ? '<div class="algolia-section">'
        : '<div class="algolia-section algolia-section--no-border-top">';
      needsDividerBeforeNextSection = true;
      html += '<div class="algolia-section__header">';
      html += '<span class="algolia-section__title">Blogs</span>';
      html +=
        '<button type="button" class="algolia-section__viewall" data-switch-tab="blogs" data-analytics-module="searchResultsSection:blog" data-analytics-link-title="viewAllBlogs" data-link-text="link">VIEW ALL</button>';
      html += '</div>';
      html += '<div class="algolia-section__scroll-wrap">';
      html += '<div class="algolia-article-grid" data-carousel-track="blogs">';
      blogHits.forEach(function (hit) {
        html += renderArticleCard(hit, 'blog');
      });
      html += '</div>';
      html += renderCarouselNav('blogs', blogHits.length);
      html += '</div>';
      html += '</div>';
    }

    // Pages section — full width
    var pgData = tabData.allTabPages;
    if (pgData && pgData.hits && pgData.hits.length > 0) {
      if (needsDividerBeforeNextSection) html += '<div class="algolia-divider"></div>';
      html += needsDividerBeforeNextSection
        ? '<div class="algolia-section">'
        : '<div class="algolia-section algolia-section--no-border-top">';
      needsDividerBeforeNextSection = true;
      html += '<div class="algolia-section__header">';
      html += '<span class="algolia-section__title">Pages</span>';
      html +=
        '<button type="button" class="algolia-section__viewall" data-switch-tab="pages" data-analytics-module="searchResultsSection:page" data-analytics-link-title="viewAllPages" data-link-text="link">VIEW ALL</button>';
      html += '</div>';
      html += '<div class="algolia-page-grid">';
      var allTabPageHits = pgData.hits.slice(0, 5);
      allTabPageHits.forEach(function (hit) {
        html += renderPageCard(hit);
      });
      html += '</div>';
      html += '</div>';
    }

    container.innerHTML = html;
    loadPageImagesForContainer(container, allTabPageHits);
    // GLO fetches each card's own variant data from the same /products/{handle}.js
    // endpoint enhanceCardsAfterGlo() uses below. Calling it first gives GLO's requests
    // priority for the WebView's limited concurrent connections per host, instead of
    // competing with our own fetches for the same visible cards.
    reinitGloSwatches();
    enhanceCardsAfterGlo(container);
    addClickTrackingToProducts(container, config.productsIndex);
    if (data && data.hits && data.hits.length > WEBVIEW_GLO_BATCH_SIZE && allTabIsWebview) {
      revealRemainingProductCards(container, '.algolia-product-grid', data.hits.slice(WEBVIEW_GLO_BATCH_SIZE));
    }
    bindSectionCarousels(container);
    bindInlineSidebar(container, 'products');
    bindViewAllLinks(container);
    bindSortDropdown(container, 'products');
    bindPagination(container);
  }

  // Shared by the desktop sidebar's checkbox/remove/clear-all handlers AND
  // the mobile drawer's APPLY button — previously the drawer duplicated this
  // logic inline but dropped persistUrlState() and the page reset, which is
  // why applying a filter on mobile updated the pill/state in memory (so it
  // could look right locally) but never wrote it to the URL the way the
  // desktop sidebar does, and a page reload would lose it entirely. Keeping
  // one implementation means the two surfaces can't drift apart again.
  function reloadTabAndRender(reloadTab) {
    currentPage[getPageScope(reloadTab)] = 0;
    persistUrlState(true);
    return loadTabData(reloadTab, 0).then(function () {
      renderTabs();
      renderMobileTabSelector();
      renderSidebar();
      renderCurrentTab();

      // Publish searchResults event after filter/sort reload
      // (after renderCurrentTab so updatePageTitle has run)
      var scope = getAnalyticsScope(reloadTab);
      publishSearchResultsEvent(scope);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  function bindInlineSidebar(container, tabKey) {
    var inlineSidebar = container.querySelector('.algolia-all-sidebar');
    if (!inlineSidebar) return;
    var reloadTab = tabKey || 'products';
    var scope = getFilterScope(reloadTab);
    var scopedFilters = activeFilters[scope];

    var reload = function () {
      reloadTabAndRender(reloadTab);
    };

    inlineSidebar.querySelectorAll('.algolia-filter-checkbox').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var attr = cb.getAttribute('data-attr');
        var value = cb.getAttribute('data-value');
        var linkTitle = attr.toLowerCase() + '::' + String(value).toLowerCase();

        if (!scopedFilters[attr]) scopedFilters[attr] = [];
        if (cb.checked) {
          scopedFilters[attr].push(value);

          // Publish facet apply click event
          publishClickEvent('productListing:facet', linkTitle, 'filterApply');
        } else {
          // Publish facet remove click event when unchecking
          publishClickEvent('productListing:facet::filterRemove', linkTitle, 'link');

          scopedFilters[attr] = scopedFilters[attr].filter(function (v) {
            return v !== value;
          });
        }
        if (scopedFilters[attr].length === 0) delete scopedFilters[attr];
        reload();
      });
    });

    // Query on `container` rather than `inlineSidebar`: the same current-
    // refinements markup is now also rendered in the mobile header bar
    // (.algolia-mobile-current-refinements, visible <749px, alongside the
    // desktop sidebar copy which is hidden at that width) so both copies
    // need their remove/clear-all buttons bound, not just the sidebar's.
    container.querySelectorAll('.algolia-current-refinement__remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var attr = btn.getAttribute('data-attr');
        var value = btn.getAttribute('data-value');

        // Publish facet remove click event when removing pill
        var linkTitle = attr.toLowerCase() + '::' + String(value).toLowerCase();
        publishClickEvent('productListing:facet::filterRemove', linkTitle, 'link');

        if (scopedFilters[attr]) {
          scopedFilters[attr] = scopedFilters[attr].filter(function (v) {
            return v !== value;
          });
        }
        if (scopedFilters[attr] && scopedFilters[attr].length === 0) delete scopedFilters[attr];
        reload();
      });
    });

    container.querySelectorAll('.algolia-current-refinements__clear-all').forEach(function (clearAllBtn) {
      clearAllBtn.addEventListener('click', function () {
        // Publish clear all filters click event
        publishClickEvent('productListing:facet', 'clearAllFilters', 'filterClear');

        // Only clears this tab's own scope — filters on other tabs untouched.
        activeFilters[scope] = {};
        reload();
      });
    });

    // "Clear all filters" inside renderNoFilteredResults() — shown in place
    // of the grid when this tab's active filters produced zero hits.
    container.querySelectorAll('.algolia-no-filtered-results__clear').forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Publish clear all filters click event
        publishClickEvent('productListing:facet::filterClear', 'clearAllFilters', 'link');

        activeFilters[scope] = {};
        reload();
      });
    });

    inlineSidebar.querySelectorAll('.algolia-filter-toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var facet = btn.getAttribute('data-facet');
        expandedFacets[facet] = !expandedFacets[facet];
        renderCurrentTab();
      });
    });

    inlineSidebar.querySelectorAll('[data-collapse-facet]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var facet = btn.getAttribute('data-collapse-facet');
        collapsedFacets[facet] = !collapsedFacets[facet];
        renderCurrentTab();
      });
    });
  }

  // ─── Render: Products tab ───────────────────────────────────────────

  function renderProductsTab(container) {
    var data = tabData.products;
    var html = '';

    // A failed products search must not fall through to the filter/empty
    // states below — both would tell the shopper nothing matched, when in
    // fact nothing was checked.
    if (productsSearchFailed()) {
      container.innerHTML = renderSearchError();
      return;
    }

    // Header/sidebar/mobile-filter-bar render whenever the tab has data at
    // all, regardless of hit count, so an active filter that zeroes out
    // results still leaves the user a way to see and clear it (see
    // renderNoFilteredResults). Only a hard "no data loaded" case falls
    // back to the full-page empty state.
    if (data) {
      var productsFacetsHtml = buildSidebarHtml('products');
      var productsHasFacets = hasFacetsToShow(productsFacetsHtml);

      html +=
        '<div class="algolia-all-products-section' +
        (productsHasFacets ? '' : ' algolia-all-products-section--no-facets') +
        '">';

      if (productsHasFacets) {
        html += '<div class="algolia-all-sidebar">';
        html += buildCurrentRefinementsHtml('products');
        html += productsFacetsHtml;
        html += '</div>';
      }

      html += '<div class="algolia-all-products-main">';
      html += '<div class="algolia-results-header">';
      html += '<div class="algolia-results-header__left">';
      html += '<div class="algolia-search-result">Search results for <b>"' + currentQuery + '"</b></div>';
      html += '<div class="algolia-results-stats">' + data.nbHits + ' results found</div>';
      html +=
        '<div class="algolia-mobile-filter-btn">' +
        buildMobileFilterButtonHtml() +
        buildMobileCurrentRefinementsHtml('products') +
        '</div>';
      html += '</div>';
      html += renderSortDropdown();
      html += '</div>';

      var productsIsWebview = document.documentElement.classList.contains('webview');
      if (data.hits && data.hits.length > 0) {
        html +=
          '<div class="algolia-product-grid' + (productsHasFacets ? '' : ' algolia-product-grid--no-facets') + '">';
        var productsInitialHits = productsIsWebview ? data.hits.slice(0, WEBVIEW_GLO_BATCH_SIZE) : data.hits;
        productsInitialHits.forEach(function (hit) {
          html += renderProductCard(hit);
        });
        html += '</div>';
        html += renderPagination(data.page, data.nbPages);
      } else {
        html += renderNoFilteredResults('products');
      }
      html += '</div>';

      html += '</div>';
    } else {
      html += renderEmptyState();
    }

    container.innerHTML = html;
    // See renderAllTab() — reinitGloSwatches() runs first so GLO's own per-card fetches
    // (same /products/{handle}.js endpoint as the calls below) get priority for the
    // WebView's limited concurrent connections instead of queuing behind ours.
    reinitGloSwatches();
    enhanceCardsAfterGlo(container);
    addClickTrackingToProducts(container, config.productsIndex);
    if (data && data.hits && data.hits.length > WEBVIEW_GLO_BATCH_SIZE && productsIsWebview) {
      revealRemainingProductCards(container, '.algolia-product-grid', data.hits.slice(WEBVIEW_GLO_BATCH_SIZE));
    }
    bindInlineSidebar(container, 'products');
    bindSortDropdown(container, 'products');
    bindPagination(container);
  }

  // ─── Render: Collections tab ────────────────────────────────────────

  function renderCollectionsTab(container) {
    var data = tabData.collections;
    var html = '';

    if (data && data.hits && data.hits.length > 0) {
      html += '<div class="algolia-results-header sort-column">';
      html += '<div class="algolia-search-result">Search results for <b>"' + currentQuery + '"</b></div>';
      html += '<div class="algolia-results-stats">' + data.nbHits + ' results found</div>';
      html += '</div>';
      html += '<div class="algolia-collection-grid algolia-collection-grid--full">';
      data.hits.forEach(function (hit) {
        html += renderCollectionCard(hit);
      });
      html += '</div>';
      if (data.nbPages > 1) {
        html += renderPagination(data.page, data.nbPages);
      }
    } else {
      html += renderEmptyState();
    }

    container.innerHTML = html;
    bindPagination(container);
  }

  // ─── Render: Recipes tab ────────────────────────────────────────────

  function renderRecipesTab(container) {
    var data = tabData.recipes;
    var html = '';

    if (data) {
      var recipesFacetsHtml = buildArticleSidebarHtml(data, 'recipes');
      var recipesHasFacets = hasFacetsToShow(recipesFacetsHtml);

      html +=
        '<div class="algolia-all-products-section' +
        (recipesHasFacets ? '' : ' algolia-all-products-section--no-facets') +
        '">';

      if (recipesHasFacets) {
        html += '<div class="algolia-all-sidebar">';
        html += buildCurrentRefinementsHtml('recipes');
        html += recipesFacetsHtml;
        html += '</div>';
      }

      html += '<div class="algolia-all-products-main">';
      html += '<div class="algolia-results-header">';
      html += '<div class="algolia-results-header__left">';
      html += '<div class="algolia-search-result">Search results for <b>"' + currentQuery + '"</b></div>';
      html += '<div class="algolia-results-stats">' + data.nbHits + ' results found</div>';
      html +=
        '<div class="algolia-mobile-filter-btn">' +
        buildMobileFilterButtonHtml() +
        buildMobileCurrentRefinementsHtml('recipes') +
        '</div>';
      html += '</div>';
      html += renderSortDropdown('recipes');
      html += '</div>';

      if (data.hits && data.hits.length > 0) {
        html +=
          '<div class="algolia-article-grid algolia-article-grid--full' +
          (recipesHasFacets ? '' : ' algolia-article-grid--no-facets') +
          '">';
        data.hits.forEach(function (hit) {
          html += renderArticleCard(hit, 'recipe');
        });
        html += '</div>';
        if (data.nbPages > 1) {
          html += renderPagination(data.page, data.nbPages);
        }
      } else {
        html += renderNoFilteredResults('recipes');
      }
      html += '</div>';

      html += '</div>';
    } else {
      html += renderEmptyState();
    }

    container.innerHTML = html;
    bindInlineSidebar(container, 'recipes');
    bindSortDropdown(container, 'recipes');
    bindPagination(container);
  }

  // ─── Render: Blogs tab ──────────────────────────────────────────────

  function renderBlogsTab(container) {
    var data = tabData.blogs;
    var html = '';

    if (data) {
      var blogsFacetsHtml = buildArticleSidebarHtml(data, 'blogs');
      var blogsHasFacets = hasFacetsToShow(blogsFacetsHtml);

      html +=
        '<div class="algolia-all-products-section' +
        (blogsHasFacets ? '' : ' algolia-all-products-section--no-facets') +
        '">';

      if (blogsHasFacets) {
        html += '<div class="algolia-all-sidebar">';
        html += buildCurrentRefinementsHtml('blogs');
        html += blogsFacetsHtml;
        html += '</div>';
      }

      html += '<div class="algolia-all-products-main">';
      html += '<div class="algolia-results-header">';
      html += '<div class="algolia-results-header__left">';
      html += '<div class="algolia-search-result">Search results for <b>"' + currentQuery + '"</b></div>';
      html += '<div class="algolia-results-stats">' + data.nbHits + ' results found</div>';
      html +=
        '<div class="algolia-mobile-filter-btn">' +
        buildMobileFilterButtonHtml() +
        buildMobileCurrentRefinementsHtml('blogs') +
        '</div>';
      html += '</div>';
      html += renderSortDropdown('blogs');
      html += '</div>';

      if (data.hits && data.hits.length > 0) {
        html +=
          '<div class="algolia-article-grid algolia-article-grid--full' +
          (blogsHasFacets ? '' : ' algolia-article-grid--no-facets') +
          '">';
        data.hits.forEach(function (hit) {
          html += renderArticleCard(hit, 'blog');
        });
        html += '</div>';
        if (data.nbPages > 1) {
          html += renderPagination(data.page, data.nbPages);
        }
      } else {
        html += renderNoFilteredResults('blogs');
      }
      html += '</div>';

      html += '</div>';
    } else {
      html += renderEmptyState();
    }

    container.innerHTML = html;
    bindInlineSidebar(container, 'blogs');
    bindSortDropdown(container, 'blogs');
    bindPagination(container);
  }

  // ─── Render: Pages tab ──────────────────────────────────────────────

  function renderPagesTab(container) {
    var data = tabData.pages;
    var html = '';

    if (data && data.hits && data.hits.length > 0) {
      html += '<div class="algolia-results-header sort-column">';
      html += '<div class="algolia-search-result">Search results for <b>"' + currentQuery + '"</b></div>';
      html += '<div class="algolia-results-stats">' + data.nbHits + ' results found</div>';
      html += '</div>';
      html += '<div class="algolia-page-grid algolia-page-grid--full">';
      data.hits.forEach(function (hit) {
        html += renderPageCard(hit);
      });
      html += '</div>';
      if (data.nbPages > 1) {
        html += renderPagination(data.page, data.nbPages);
      }
    } else {
      html += renderEmptyState();
    }

    container.innerHTML = html;
    if (data && data.hits) loadPageImagesForContainer(container, data.hits);
    bindPagination(container);
  }

  // ─── Badge helpers ──────────────────────────────────────────────────

  function getBadgeForHit(hit) {
    var configs = window.productBadgeConfigs;
    if (!configs || !configs.length) return null;

    var tags = Array.isArray(hit.tags) ? hit.tags : [];
    var publishedAt = hit.published_at;
    var now = Date.now();

    for (var i = 0; i < configs.length; i++) {
      var badge = configs[i];

      // Tag condition
      if (badge.condition_tag_name) {
        if (tags.indexOf(badge.condition_tag_name) === -1) continue;
      }

      // Inventory cap condition
      if (badge.condition_inventory_cap != null && badge.condition_inventory_cap !== '') {
        var cap = badge.condition_inventory_cap;
        var qty = hit.inventory_quantity != null ? hit.inventory_quantity : null;
        var totalQty = hit.variants_inventory_count != null ? hit.variants_inventory_count : null;
        var isOversellAllowed = hit.inventory_policy === 'continue';
        if (qty == null) continue;
        if (isOversellAllowed) continue;
        if (badge.condition_inventory_cap_for_all_variants) {
          if (totalQty == null || totalQty > cap || (cap > 0 && totalQty <= 0)) continue;
        } else {
          if (cap > 0 && qty <= 0) continue;
          if (qty > cap) continue;
        }
      }

      // Publish date range condition
      if (badge.condition_publish_date_range != null && badge.condition_publish_date_range !== '') {
        if (publishedAt) {
          var diffDays = (now - new Date(publishedAt).getTime()) / (1000 * 60 * 60 * 24);
          if (diffDays > badge.condition_publish_date_range) continue;
        }
      }

      return badge;
    }
    return null;
  }

  function renderBadgeHtml(badge) {
    if (!badge) return '';
    // image_url is resolved server-side in main-search.liquid (Liquid's
    // image_url filter) since the raw metaobject reference only serializes
    // to a GID string, not a usable URL, when passed through `| json`.
    if (badge.image_url) {
      return (
        '<div class="flair"><img src="' +
        escapeHtml(badge.image_url) +
        '" alt="' +
        escapeHtml(badge.display_text || '') +
        '" width="75" height="75" loading="lazy"></div>'
      );
    }
    var fontWeight = badge.text_bold ? 'bold' : 'normal';
    return (
      '<div class="badge color-scheme-grey" style="background-color:' +
      escapeHtml(badge.background_color || '') +
      ';color:' +
      escapeHtml(badge.text_color || '') +
      ';font-weight:' +
      fontWeight +
      ';">' +
      escapeHtml(badge.display_text || '') +
      '</div>'
    );
  }

  // ─── Render: Product card ───────────────────────────────────────────

  function renderProductCard(hit) {
    var h = hit.handle || '';
    var url = hit.url || '/products/' + h;
    var img = hit.image || '';
    var price = hit.price;
    var compareAt = hit.compare_at_price;
    var variantsCount = hit.variants_count || 1;
    var isMultiVariant = variantsCount > 1;
    // Badge
    var badgeHtml = renderBadgeHtml(getBadgeForHit(hit));

    // Price — variants_min_price and variants_compare_at_price_max are root-level in Algolia Shopify hits
    var minPrice = hit.variants_min_price || price;
    var compareAtFinal = hit.variants_compare_at_price_max || compareAt;
    var isOnSaleFinal = compareAtFinal && compareAtFinal > 0 && minPrice < compareAtFinal;
    var priceHtml = '';
    if (isOnSaleFinal) {
      var prefix = isMultiVariant ? 'From ' : '';
      priceHtml =
        '<span class="hit-price--sale">' +
        prefix +
        formatPrice(minPrice) +
        '</span>' +
        '<s class="hit-price--compare">' +
        formatPrice(compareAtFinal) +
        '</s>';
    } else if (isMultiVariant) {
      priceHtml = '<span class="hit-price">From ' + formatPrice(minPrice) + '</span>';
    } else if (price) {
      priceHtml = '<span class="hit-price">' + formatPrice(price) + '</span>';
    }

    // Placeholder — filled with real counts by enhanceOptionHintsForCard() via /products/{handle}.js fetch
    var variantHint = '<span class="hit-variant-hint" data-option-hint style="display:none"></span>';

    // Use Algolia's highlight result for the title if available
    var titleHtml = '';
    if (hit._highlightResult && hit._highlightResult.title && hit._highlightResult.title.value) {
      // Algolia wraps matched words in <em> — convert to <mark> for our styling
      titleHtml = hit._highlightResult.title.value.replace(/<em>/g, '<mark>').replace(/<\/em>/g, '</mark>');
    } else {
      titleHtml = escapeHtml(hit.title || '');
    }

    // Quick view — product ID used for unique modal/button IDs
    var productId = hit.id || hit.objectID || h;
    var productUrl = '/products/' + escapeHtml(h);
    var spinnerSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>';
    var closeSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" width="24" height="24" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>';

    var quickViewBtn =
      '<div class="quick-add no-js-hidden color-scheme-light-linen">' +
      '<modal-opener data-modal="#QuickAdd-' +
      productId +
      '">' +
      '<button' +
      ' id="quick-add-' +
      productId +
      '-submit"' +
      ' type="button"' +
      ' class="quick-add__submit button button--full-width button--secondary"' +
      ' aria-haspopup="dialog"' +
      ' data-product-url="' +
      productUrl +
      '"' +
      '>Quick View<div class="loading__spinner hidden">' +
      spinnerSvg +
      '</div></button>' +
      '</modal-opener>' +
      '</div>';

    var quickAddModal =
      '<quick-add-modal id="QuickAdd-' +
      productId +
      '" class="quick-add-modal">' +
      '<div role="dialog"' +
      ' aria-label="' +
      escapeHtml(hit.title || '') +
      '"' +
      ' aria-modal="true"' +
      ' class="quick-add-modal__content global-settings-popup"' +
      ' tabindex="-1">' +
      '<button id="ModalClose-' +
      productId +
      '" type="button" class="quick-add-modal__toggle" aria-label="Close">' +
      closeSvg +
      '</button>' +
      '<div id="QuickAddInfo-' +
      productId +
      '" class="quick-add-modal__content-info"></div>' +
      '</div>' +
      '</quick-add-modal>';

    return (
      '<div class="hit-wrapper" data-product-handle="' +
      escapeHtml(h) +
      '" data-object-id="' +
      escapeHtml(String(hit.objectID || hit.id || '')) +
      '">' +
      '<div class="hit-media">' +
      '<a class="hit-image-link" href="' +
      escapeHtml(url) +
      '" data-analytics-module="productListing:grid" data-analytics-link-title="' +
      escapeHtml(hit.title || '') +
      '" data-link-text="link">' +
      '<div class="hit-image">' +
      '<img src="' +
      escapeHtml(img || PLACEHOLDER_IMAGE) +
      '" alt="' +
      escapeHtml(hit.title || '') +
      '" loading="lazy" width="200" height="200">' +
      '</div>' +
      badgeHtml +
      '</a>' +
      quickViewBtn +
      '</div>' +
      '<div class="hit-body">' +
      '<h3 class="hit-title">' +
      '<a class="hit-info" href="' +
      escapeHtml(url) +
      '" data-analytics-module="productListing:grid" data-analytics-link-title="' +
      escapeHtml(hit.title || '') +
      '" data-link-text="link">' +
      titleHtml +
      '</a>' +
      '</h3>' +
      '<div class="hit-price-row">' +
      priceHtml +
      '</div>' +
      variantHint +
      '</div>' +
      quickAddModal +
      '</div>'
    );
  }

  // ─── Render: Collection card ────────────────────────────────────────

  function renderCollectionCard(hit) {
    var url = '/collections/' + (hit.handle || '');
    var img = hit.image || '';

    return (
      '<a class="hit hit--collection" data-object-id="' +
      escapeHtml(String(hit.objectID || '')) +
      '" href="' +
      escapeHtml(url) +
      '" data-analytics-module="searchResultsSection:collection" data-analytics-link-title="' +
      escapeHtml(hit.title || '') +
      '" data-link-text="link">' +
      '<div class="hit-image hit-image--collection">' +
      '<img src="' +
      escapeHtml(img || PLACEHOLDER_IMAGE) +
      '" alt="' +
      escapeHtml(hit.title || '') +
      '" loading="lazy" width="200" height="200">' +
      '</div>' +
      '<div class="hit-body">' +
      '<h3 class="hit-title">' +
      escapeHtml(hit.title || '') +
      '</h3>' +
      '</div>' +
      '</a>'
    );
  }

  // ─── Render: Article card (recipes + blogs) ─────────────────────────

  function renderArticleCard(hit, type) {
    var blogHandle = (hit.blog && hit.blog.handle) || (type === 'recipe' ? 'recipe' : 'article');
    var url = '/blogs/' + blogHandle + '/' + (hit.handle || '');
    var img = hit.image || '';
    var date = formatDate(hit.published_at);
    var moduleName = type === 'recipe' ? 'searchResultsSection:recipe' : 'searchResultsSection:blog';

    return (
      '<a class="hit hit--article" data-object-id="' +
      escapeHtml(String(hit.objectID || '')) +
      '" href="' +
      escapeHtml(url) +
      '" data-analytics-module="' +
      moduleName +
      '" data-analytics-link-title="' +
      escapeHtml(hit.title || '') +
      '" data-link-text="link">' +
      '<div class="hit-image hit-image--article">' +
      '<img src="' +
      escapeHtml(img || PLACEHOLDER_IMAGE) +
      '" alt="' +
      escapeHtml(hit.title || '') +
      '" loading="lazy" width="200" height="200">' +
      '</div>' +
      '<div class="hit-body">' +
      '<h3 class="hit-title">' +
      escapeHtml(hit.title || '') +
      '</h3>' +
      (date ? '<span class="hit-date">' + date + '</span>' : '') +
      '</div>' +
      '</a>'
    );
  }

  // ─── Render: Page card ──────────────────────────────────────────────

  // custom.landing_page_image is a file_reference metafield; Algolia's page
  // hits carry no usable image URL for it. Each visible page hit's image is
  // resolved on demand here, batched into one Storefront API request per
  // render — a direct page(handle:) lookup has no page-count limit, unlike
  // walking every page on the store server-side.
  var pageImageCache = {}; // handle -> resolved URL, or null if confirmed absent
  var storefrontConfigCache;

  function getStorefrontConfig() {
    if (storefrontConfigCache !== undefined) return storefrontConfigCache;
    var container = document.getElementById('algolia-instantsearch');
    var raw = container && container.dataset.storefrontConfig;
    try {
      storefrontConfigCache = raw ? JSON.parse(raw) : null;
    } catch {
      storefrontConfigCache = null;
    }
    return storefrontConfigCache;
  }

  function resolvePageImages(handles) {
    var uncached = handles.filter(function (h) {
      return h && !(h in pageImageCache);
    });
    if (!uncached.length) return Promise.resolve();

    var cfg = getStorefrontConfig();
    if (!cfg || !cfg.storefrontApiAccessToken) {
      // Cache as null (not just skip) so a missing/misconfigured token
      // doesn't retry the same failing lookup on every card render.
      uncached.forEach(function (h) {
        pageImageCache[h] = null;
      });
      return Promise.resolve();
    }

    // Aliased by index (h0, h1, ...), not by handle — GraphQL field/alias
    // names can't contain hyphens, which page handles routinely do.
    var variables = {};
    var fields = uncached.map(function (h, i) {
      variables['h' + i] = h;
      return (
        'h' +
        i +
        ': page(handle: $h' +
        i +
        ') { handle metafield(namespace: "custom", key: "landing_page_image") { reference { ... on MediaImage { image { url } } } } }'
      );
    });
    var query =
      'query GetPageImages(' +
      uncached
        .map(function (h, i) {
          return '$h' + i + ': String!';
        })
        .join(', ') +
      ') { ' +
      fields.join(' ') +
      ' }';

    return fetch(cfg.domain + '/api/' + cfg.storefrontApiVersion + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.storefrontApiAccessToken,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        var data = json && json.data;
        uncached.forEach(function (h, i) {
          var node = data && data['h' + i];
          var ref = node && node.metafield && node.metafield.reference;
          pageImageCache[h] = (ref && ref.image && ref.image.url) || null;
        });
      })
      .catch(function () {
        uncached.forEach(function (h) {
          pageImageCache[h] = null;
        });
      });
  }

  function applyResolvedPageImages(container) {
    var imgs = container.querySelectorAll('img[data-page-handle]');
    imgs.forEach(function (img) {
      var handle = img.getAttribute('data-page-handle');
      img.src = pageImageCache[handle] || PLACEHOLDER_IMAGE;
      img.removeAttribute('data-page-handle');
    });
  }

  function loadPageImagesForContainer(container, hits) {
    var handles = (hits || [])
      .map(function (hit) {
        return hit && hit.handle;
      })
      .filter(Boolean);
    if (!handles.length) return;
    resolvePageImages(handles).then(function () {
      applyResolvedPageImages(container);
    });
  }

  function renderPageCard(hit) {
    var url = '/pages/' + (hit.handle || '');
    var excerpt = hit.body_html_safe ? stripHtml(hit.body_html_safe).substring(0, 120) : '';
    var handle = hit.handle || '';
    var isCached = handle && handle in pageImageCache;
    // Show the placeholder immediately, even while a lookup is still
    // pending, so the card never renders blank — the fetched image simply
    // replaces it in place once resolved.
    var imgSrc = isCached ? pageImageCache[handle] || PLACEHOLDER_IMAGE : PLACEHOLDER_IMAGE;
    var pendingAttr = handle && !isCached ? ' data-page-handle="' + escapeHtml(handle) + '"' : '';

    return (
      '<a class="hit hit--page" data-object-id="' +
      escapeHtml(String(hit.objectID || '')) +
      '" href="' +
      escapeHtml(url) +
      '">' +
      '<div class="hit-image hit-image--page">' +
      '<img src="' +
      escapeHtml(imgSrc) +
      '" alt="' +
      escapeHtml(hit.title || '') +
      '" loading="lazy" decoding="async" width="200" height="200"' +
      pendingAttr +
      '>' +
      '</div>' +
      '<div class="hit-body">' +
      '<h3 class="hit-title">' +
      escapeHtml(hit.title || '') +
      '</h3>' +
      (excerpt ? '<p class="hit-excerpt">' + escapeHtml(excerpt) + '</p>' : '') +
      '</div>' +
      '</a>'
    );
  }

  // ─── Render: Sort dropdown ──────────────────────────────────────────

  function renderSortDropdown(scope) {
    scope = scope || 'products';
    var options =
      scope === 'recipes' || scope === 'blogs'
        ? [
            { label: 'Most relevant', value: 'default' },
            { label: 'Date, new to old', value: 'newest' },
            { label: 'Date, old to new', value: 'oldest' },
            { label: 'Alphabetically, A-Z', value: 'title_asc' },
            { label: 'Alphabetically, Z-A', value: 'title_desc' },
          ]
        : [
            { label: 'Most relevant', value: 'default' },
            { label: 'Best selling', value: 'best_selling' },
            { label: 'Alphabetically, A-Z', value: 'title_asc' },
            { label: 'Alphabetically, Z-A', value: 'title_desc' },
            { label: 'Price, low to high', value: 'price_asc' },
            { label: 'Price, high to low', value: 'price_desc' },
            { label: 'Date, old to new', value: 'oldest' },
            { label: 'Date, new to old', value: 'newest' },
          ];
    var html = '<div class="algolia-sort" data-sort-scope="' + scope + '">';
    html += '<label class="algolia-sort__label">Sort by:</label>';
    html += '<select class="algolia-sort__select">';
    options.forEach(function (opt) {
      var selected = opt.value === currentSort[scope] ? ' selected' : '';
      html += '<option value="' + opt.value + '"' + selected + '>' + opt.label + '</option>';
    });
    html += '</select>';
    html += '</div>';
    return html;
  }

  function bindSortDropdown(container, scope) {
    scope = scope || 'products';
    var select = container.querySelector('.algolia-sort__select');
    if (!select) return;

    var options =
      scope === 'recipes' || scope === 'blogs'
        ? [
            { label: 'Relevance', value: 'default' },
            { label: 'Newest', value: 'newest' },
            { label: 'Oldest', value: 'oldest' },
            { label: 'Title: A-Z', value: 'title_asc' },
            { label: 'Title: Z-A', value: 'title_desc' },
          ]
        : [
            { label: 'Relevance', value: 'default' },
            { label: 'Best Selling', value: 'best_selling' },
            { label: 'Title: A-Z', value: 'title_asc' },
            { label: 'Title: Z-A', value: 'title_desc' },
            { label: 'Price: Low to High', value: 'price_asc' },
            { label: 'Price: High to Low', value: 'price_desc' },
            { label: 'Oldest', value: 'oldest' },
            { label: 'Newest', value: 'newest' },
          ];

    var isSortOpen = false;
    var handleSortOpen = function () {
      if (isSortOpen) return;
      isSortOpen = true;
      publishClickEvent('productListing:sort', 'sortOpen', 'open');
    };

    select.addEventListener('mousedown', handleSortOpen);
    select.addEventListener('touchstart', handleSortOpen, { passive: true });
    select.addEventListener('keydown', function (e) {
      if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        handleSortOpen();
      }
    });
    select.addEventListener('blur', function () {
      isSortOpen = false;
    });

    select.addEventListener('change', function (e) {
      isSortOpen = false;
      currentSort[scope] = e.target.value;
      var reloadTab = scope; // 'products' | 'recipes' | 'blogs'
      currentPage[reloadTab] = 0;

      // Publish sort change click event
      var selectedOption = options.find(function (opt) {
        return opt.value === e.target.value;
      });
      var selectedLabel = selectedOption ? selectedOption.label : e.target.value;
      publishClickEvent('productListing:sort', selectedLabel, 'change');

      persistUrlState(true);
      loadTabData(reloadTab, 0).then(function () {
        renderTabs();
        renderMobileTabSelector();
        renderSidebar();
        renderCurrentTab();

        // Publish searchResults event after sort change
        // (after renderCurrentTab so updatePageTitle has run)
        var filterScope = getAnalyticsScope(reloadTab);
        publishSearchResultsEvent(filterScope);
      });
    });
  }

  // ─── Render: Pagination ─────────────────────────────────────────────

  function renderPagination(currentPageNum, totalPages) {
    if (totalPages <= 1) return '';

    var parts = buildPaginationParts(currentPageNum, totalPages);
    var html = '<div class="pagination-wrapper"><nav class="pagination" role="navigation" aria-label="Pagination">';
    html += '<ul class="pagination__list list-unstyled" role="list">';

    // Previous
    if (currentPageNum > 0) {
      html +=
        '<li><button class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-page="' +
        (currentPageNum - 1) +
        '" aria-label="Previous page">' +
        '<span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></span>' +
        '</button></li>';
    }

    parts.forEach(function (part) {
      if (part.type === 'ellipsis') {
        html += '<li><span class="pagination__item">&hellip;</span></li>';
      } else if (part.type === 'current') {
        html +=
          '<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ' +
          part.display +
          '">' +
          part.display +
          '</a></li>';
      } else {
        html +=
          '<li><button class="pagination__item link" data-page="' +
          part.algoliaPage +
          '" aria-label="Page ' +
          part.display +
          '">' +
          part.display +
          '</button></li>';
      }
    });

    // Next
    if (currentPageNum < totalPages - 1) {
      html +=
        '<li><button class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-page="' +
        (currentPageNum + 1) +
        '" aria-label="Next page">' +
        '<span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></span>' +
        '</button></li>';
    }

    html += '</ul></nav></div>';
    return html;
  }

  function buildPaginationParts(current, total) {
    var currentDisplay = current + 1; // 1-indexed
    var delta = 2;
    var parts = [];
    var i;

    // Always include page 1
    parts.push(1);

    // Pages before the window: add ellipsis if there's a gap
    var windowStart = Math.max(2, currentDisplay - delta);
    if (windowStart > 2) {
      parts.push('ellipsis');
    }

    // Window: 2 before current, current, 2 after current
    for (i = windowStart; i <= Math.min(total, currentDisplay + delta); i++) {
      if (i !== 1 && i !== total) {
        parts.push(i);
      }
    }

    // Pages after the window: add ellipsis if there's a gap
    var windowEnd = Math.min(total - 1, currentDisplay + delta);
    if (windowEnd < total - 1) {
      parts.push('ellipsis');
    }

    // Always include last page (if more than 1 page)
    if (total > 1) {
      parts.push(total);
    }

    // Convert to objects
    return parts.map(function (p) {
      if (p === 'ellipsis') {
        return { type: 'ellipsis' };
      }
      return {
        type: p === currentDisplay ? 'current' : 'page',
        display: p,
        algoliaPage: p - 1,
      };
    });
  }

  function bindPagination(container) {
    container.querySelectorAll('.pagination__item[data-page]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var page = parseInt(btn.getAttribute('data-page'), 10);
        var pageNumber = String(page + 1); // Convert to 1-indexed for display

        // Publish pagination click event
        publishClickEvent('productListing:pagination', pageNumber, 'link');

        currentPage[getPageScope(currentTab)] = page;
        persistUrlState(true);
        loadTabData(currentTab, page).then(function () {
          renderCurrentTab();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    });
  }

  // ─── Render: Empty state ────────────────────────────────────────────

  // True zero-results-anywhere state: no products, collections, recipes,
  // blogs, or pages matched the query. No tabs/sidebar/sort render around
  // this — see the `!hasAnyResults` branches that call it.
  function renderEmptyState() {
    var html = '<div class="algolia-no-results-state" role="status" aria-live="polite">';
    html += '<h2 class="algolia-no-results-state__title">No results found</h2>';
    if (currentQuery) {
      html +=
        '<p class="algolia-no-results-state__subtitle">We couldn\'t find any results for "<strong>' +
        escapeHtml(currentQuery) +
        '</strong>".</p>';
    }
    html +=
      '<p class="algolia-no-results-state__helper">Try checking your spelling, using fewer keywords, or searching for a broader term.</p>';
    html += '</div>';
    return html;
  }

  // Reuses the no-results classes: like that state, this renders with no tabs or
  // sidebar around it, so it needs the centred full-page treatment rather than
  // the left-aligned one used inside the products column.
  function renderSearchError() {
    var html = '<div class="algolia-no-results-state" role="status" aria-live="polite">';
    html += '<h2 class="algolia-no-results-state__title">Something went wrong</h2>';
    html +=
      '<p class="algolia-no-results-state__subtitle">We couldn\'t load results just now. Please refresh the page or try again in a moment.</p>';
    html += '</div>';
    return html;
  }

  function renderNoProductsFound() {
    var html = '<div class="algolia-no-products-state" role="status" aria-live="polite">';
    html += '<h2 class="algolia-no-products-state__title">No products found</h2>';
    if (currentQuery) {
      html +=
        '<p class="algolia-no-products-state__subtitle">We couldn\'t find any products for "<strong>' +
        escapeHtml(currentQuery) +
        '</strong>".</p>';
    }
    html += '</div>';
    return html;
  }

  // Shown INSIDE the results grid area (not instead of the whole section)
  // when a tab's own query has zero hits but filters are still active for
  // that scope. Previously the whole products/recipes/blogs section —
  // including the filter/sort bar, the sidebar, and the mobile current-
  // refinements pills — was replaced by renderEmptyState() whenever hits
  // was empty, which meant a filter combination that zeroed out results
  // also hid the only UI (the pill's "x" / "Clear all filters") a mobile
  // visitor had to back out of it. Keeping the header+sidebar/mobile-bar
  // rendered unconditionally and swapping only this inner message fixes
  // that trap.
  function renderNoFilteredResults(scope) {
    var hasFilters = activeFilters[scope] && Object.keys(activeFilters[scope]).length > 0;
    return (
      '<div class="algolia-no-filtered-results">' +
      '<p class="algolia-no-filtered-results__title">No results match the selected filters.</p>' +
      (hasFilters
        ? '<button type="button" class="algolia-no-filtered-results__clear" data-clear-scope="' +
          scope +
          '">Clear all filters</button>'
        : '') +
      '</div>'
    );
  }

  // ─── Render: Sidebar (Products + ALL tabs only) ────────────────────

  var FACET_LABELS = {
    price_range: 'PRICE RANGE',
    price: 'PRICE',
    product_type: 'CATEGORY',
    'options.color': 'COLOR',
    'options.size': 'SIZE',
    'options.fabric': 'FABRIC',
    'meta.netsuite.color': 'COLOR',
    'meta.filters.filter_product_category.fields.name': 'CATEGORY',
    'meta.filters.filter_product_type.fields.name': 'PRODUCT TYPE',
    'blog.title': 'BLOG',
    tags: 'TAGS',
    'author.name': 'AUTHOR',
    'meta.custom.blog_categories': 'CATEGORY',
    'meta.custom.recipe_meal_types': 'RECIPE TYPE',
    'meta.custom.recipe_ingredients': 'INGREDIENT',
  };

  function facetLabel(attr) {
    if (FACET_LABELS[attr]) return FACET_LABELS[attr];
    var parts = attr.split('.');
    return parts[parts.length - 1].replace(/_/g, ' ').toUpperCase();
  }

  var FACET_VISIBLE_BY_DEFAULT = 10;
  // Off by default: variant-level index + distinct makes per-value counts undercount products.
  var SHOW_FACET_COUNTS = !!(config && config.showFacetCounts === true);

  function renderFacetGroup(facetAttr, facetData, sortFn, scope) {
    var scopedFilters = activeFilters[scope] || {};
    var selected = scopedFilters[facetAttr] || [];
    var keys = Object.keys(facetData);
    // Disjunctive counts still apply every OTHER active filter, so a
    // selected value with zero matches under those won't come back in
    // facetData at all — keep it in the list so a checked box never
    // silently disappears from the sidebar.
    selected.forEach(function (val) {
      if (keys.indexOf(val) === -1) keys.push(val);
    });
    if (keys.length === 0) return '';
    if (sortFn) keys.sort(sortFn);

    var label = facetLabel(facetAttr);
    var isExpanded = expandedFacets[facetAttr];
    var isCollapsed = collapsedFacets[facetAttr];
    var visibleKeys = isExpanded ? keys : keys.slice(0, FACET_VISIBLE_BY_DEFAULT);
    selected.forEach(function (val) {
      if (visibleKeys.indexOf(val) === -1) visibleKeys.push(val);
    });
    var hasMore = keys.length > FACET_VISIBLE_BY_DEFAULT;

    var chevronSvg =
      '<svg class="algolia-filter-chevron' +
      (isCollapsed ? ' is-collapsed' : '') +
      '" viewBox="0 0 10 6" width="10" height="10" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';

    var html = '<div class="algolia-filter-group">';
    html +=
      '<button class="algolia-filter-group__title" data-collapse-facet="' +
      facetAttr +
      '">' +
      label +
      chevronSvg +
      '</button>';

    if (!isCollapsed) {
      html += '<div class="algolia-filter-group__body">';
      html += '<ul class="algolia-filter-list">';
      visibleKeys.forEach(function (key) {
        var count = facetData[key] || 0;
        var isChecked = scopedFilters[facetAttr] && scopedFilters[facetAttr].indexOf(key) > -1;
        var displayLabel =
          key.indexOf(':') !== -1 && facetAttr.indexOf('price') !== -1
            ? '$' + key.split(':')[0] + ' - $' + key.split(':')[1]
            : facetAttr.indexOf('price') !== -1 && !isNaN(parseFloat(key))
              ? '$' + parseFloat(key).toFixed(2)
              : key;
        html += '<li class="algolia-filter-item">';
        html += '<label class="algolia-filter-label">';
        html +=
          '<input type="checkbox" class="algolia-filter-checkbox" data-attr="' +
          facetAttr +
          '" data-value="' +
          escapeHtml(key) +
          '"' +
          (isChecked ? ' checked' : '') +
          '>';
        html += '<span>' + displayLabel + '</span>';
        if (SHOW_FACET_COUNTS) html += '<span>(' + count + ')</span>';
        html += '</label>';
        html += '</li>';
      });
      html += '</ul>';

      if (hasMore) {
        var btnLabel = isExpanded ? '- Show less' : '+ Show more';
        var linkTitle = isExpanded ? 'showLess' : 'showMore';
        var linkText = isExpanded ? 'collapse' : 'expand';
        html +=
          '<button class="algolia-filter-toggle" data-facet="' +
          facetAttr +
          '" data-analytics-module="productListing:facet" data-analytics-link-title="' +
          linkTitle +
          '" data-link-text="' +
          linkText +
          '">' +
          btnLabel +
          '</button>';
      }
      html += '</div>';
    }

    html += '</div>';
    return html;
  }

  function renderSidebar() {
    var sidebar = document.getElementById('algolia-sidebar');
    if (!sidebar) return;

    // Always hidden visually — ALL/PRODUCTS/RECIPES/BLOGS use an inline sidebar
    // for desktop. Content is still populated so the mobile filter drawer can read it.
    sidebar.style.display = 'none';

    if (currentTab === 'recipes' || currentTab === 'blogs') {
      sidebar.innerHTML = buildArticleSidebarHtml(tabData[currentTab], currentTab);
      return;
    }

    if (currentTab !== 'all' && currentTab !== 'products') {
      sidebar.innerHTML = '';
      return;
    }

    sidebar.innerHTML = buildSidebarHtml(currentTab);
  }

  function buildSidebarHtml(scope) {
    scope = scope || 'products';
    var data = scope === 'all' ? tabData.allTabProducts : tabData.products;
    return buildFacetSidebarHtml(data, null, scope);
  }

  // Whether a given tab's sidebar actually has any facet groups to show.
  // A tab can have zero facets to render for reasons other than "no data" —
  // e.g. no facets configured for the index, or every facet's values were
  // filtered out via excludedAttrs — so this checks the built HTML itself
  // rather than re-deriving the same logic buildFacetSidebarHtml already
  // encapsulates. Used to decide whether to render `.algolia-all-sidebar`
  // at all, and whether the product/article grid should expand to fill
  // the space it would have taken (mirrors the Collections tab's
  // full-width, five-across grid).
  function hasFacetsToShow(facetsHtml) {
    return !!(facetsHtml && facetsHtml.trim().length > 0);
  }

  // blog.handle splits the Recipes/Blogs tabs internally (see loadTabData)
  // and must never render as a user-facing checkbox, no matter what the
  // dashboard's facet display config says to show.
  var ARTICLE_ALWAYS_HIDDEN_FACETS = ['blog.handle'];

  function buildArticleSidebarHtml(data, scope) {
    var excluded = ARTICLE_ALWAYS_HIDDEN_FACETS.slice();
    if (scope === 'recipes') {
      // Recipes tab is already scoped to blog.handle:"recipe", so blog.title
      // only ever has a single value ("Recipe") here — showing it as a
      // facet would just be one redundant checkbox. Blogs tab keeps it,
      // since blog.title varies there (Article, A Note from Jo, etc).
      excluded.push('blog.title');
    }
    var explicitAttrs = config.articleFacets && config.articleFacets[0] !== '*' ? config.articleFacets : null;
    return buildFacetSidebarHtml(data, explicitAttrs, scope, excluded);
  }

  // If attrOrder is given, facets render in that fixed order; otherwise the
  // order comes from the index's renderingContent (falls back to key order).
  // excludedAttrs, if given, is filtered out of whichever order was picked.
  function buildFacetSidebarHtml(data, attrOrder, scope, excludedAttrs) {
    if (!data || !data.facets) return '';

    var html = '';
    var priceSortFn = function (a, b) {
      return parseFloat(a.split(':')[0]) - parseFloat(b.split(':')[0]);
    };

    var rc = data.renderingContent;
    var facetOrder =
      attrOrder ||
      (rc && rc.facetOrdering && rc.facetOrdering.facets && rc.facetOrdering.facets.order
        ? rc.facetOrdering.facets.order
        : Object.keys(data.facets));

    if (excludedAttrs && excludedAttrs.length) {
      facetOrder = facetOrder.filter(function (attr) {
        return excludedAttrs.indexOf(attr) === -1;
      });
    }

    facetOrder.forEach(function (facetAttr) {
      if (!data.facets[facetAttr]) return;
      var sortFn = facetAttr.indexOf('price') !== -1 ? priceSortFn : null;
      html += renderFacetGroup(facetAttr, data.facets[facetAttr], sortFn, scope);
    });

    return html;
  }

  // ─── Render: Current refinements ────────────────────────────────────
  // Renders inline at the top of each tab's own sidebar (see the
  // `.algolia-all-sidebar` markup in renderAllTab/renderProductsTab/
  // renderRecipesTab/renderBlogsTab) — not a separate section — so pills and
  // clear-all always sit above that tab's own facet groups. Click handling
  // for the remove/clear-all buttons lives in bindInlineSidebar, alongside
  // the facet checkbox handlers, since they share the same scope/reload.

  // ─── Render: Current refinements ────────────────────────────────────

  function renderCurrentRefinements() {
    var container = document.getElementById('algolia-current-refinements');
    if (!container) return;

    // Only show on tabs that have a sidebar
    if (currentTab !== 'all' && currentTab !== 'products' && currentTab !== 'recipes' && currentTab !== 'blogs') {
      container.innerHTML = '';
      return;
    }

    // Only ever show refinements that belong to the CURRENT tab's scope, so a
    // filter picked on Blogs (e.g. "Article") never appears as a chip on
    // Products, Recipes, or any other tab.
    var scope = getFilterScope(currentTab);
    var scopedFilters = activeFilters[scope];

    var html = '';
    Object.keys(scopedFilters).forEach(function (attr) {
      scopedFilters[attr].forEach(function (val) {
        var label = attr === 'price_range' ? val.replace(':', ' - $') : val;
        html +=
          '<span class="algolia-current-refinement">' +
          label +
          '<button class="algolia-current-refinement__remove" data-attr="' +
          attr +
          '" data-value="' +
          escapeHtml(val) +
          '" aria-label="Remove filter">' +
          '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" width="12" height="12"><path d="M2 2L12 12M12 2L2 12"/></svg>' +
          '</button>' +
          '</span>';
      });
    });

    container.innerHTML = html;
    container.querySelectorAll('.algolia-current-refinement__remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var attr = btn.getAttribute('data-attr');
        var value = btn.getAttribute('data-value');
        if (scopedFilters[attr]) {
          scopedFilters[attr] = scopedFilters[attr].filter(function (v) {
            return v !== value;
          });
        }
        if (scopedFilters[attr] && scopedFilters[attr].length === 0) delete scopedFilters[attr];

        var reloadTab = getFilterScope(currentTab);
        persistUrlState(true);
        loadTabData(reloadTab, 0).then(function () {
          renderTabs();
          renderMobileTabSelector();
          renderSidebar();
          renderCurrentRefinements();
          renderCurrentTab();
        });
      });
    });
  }

  // ─── Render: Mobile filter drawer ───────────────────────────────────
  // Rebuilt to match the theme's native collection-page filter/sort bar
  // (see magnolia.com/collections/* for reference): a full-width bar
  // showing an item count, opening a drawer whose first screen lists each
  // facet as a drill-in row (e.g. Price Range, Price, Color) and whose
  // second screen shows that one facet's checkbox list with a back arrow,
  // plus a persistent Sort by control and Apply action.
  //
  // All visual styling lives in algolia-instantsearch.css (section 15,
  // "Mobile filter drawer", plus the shared .algolia-filter-* rules in
  // section 6). This file only ever emits the class names the CSS file
  // already defines — no styles are injected from JS.

  function toTitleCase(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/(^|\s)\S/g, function (c) {
        return c.toUpperCase();
      });
  }

  function buildCurrentRefinementsHtml(scope) {
    var scopedFilters = activeFilters[scope];
    if (!scopedFilters || Object.keys(scopedFilters).length === 0) return '';

    var pillsHtml = '';
    Object.keys(scopedFilters).forEach(function (attr) {
      scopedFilters[attr].forEach(function (val) {
        var label = attr === 'price_range' ? val.replace(':', ' - $') : val;
        pillsHtml +=
          '<span class="algolia-current-refinement">' +
          label +
          '<button class="algolia-current-refinement__remove" data-attr="' +
          attr +
          '" data-value="' +
          escapeHtml(val) +
          '" aria-label="Remove filter">' +
          '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" width="12" height="12"><path d="M2 2L12 12M12 2L2 12"/></svg>' +
          '</button>' +
          '</span>';
      });
    });

    return (
      '<div class="algolia-current-refinements">' +
      '<button class="algolia-current-refinements__clear-all" type="button" data-analytics-module="productListing:facet" data-analytics-link-title="clearAllFilters" data-link-text="filterClear">Clear all filters</button>' +
      '<div class="algolia-current-refinements__list">' +
      pillsHtml +
      '</div>' +
      '</div>'
    );
  }

  // Mobile-only counterpart to buildCurrentRefinementsHtml(). The sidebar
  // copy (.algolia-all-sidebar) is display:none under 749px, so mobile
  // visitors never see their selected filter pills or "Clear all filters" —
  // this renders the same markup again inside .algolia-mobile-filter-btn,
  // directly below the FILTER AND SORT button, wrapped in a class that's
  // hidden on desktop (avoids showing the pills twice) and shown only
  // <749px. Reuses the exact same .algolia-current-refinement(s) classes/
  // markup so the two copies never drift out of sync, and so
  // bindInlineSidebar's remove/clear-all handlers (bound on `container`)
  // pick this copy up too. Returns '' when there are no active filters, so
  // no empty gray bar shows.
  function buildMobileCurrentRefinementsHtml(scope) {
    var inner = buildCurrentRefinementsHtml(scope);
    if (!inner) return '';
    return '<div class="algolia-mobile-current-refinements">' + inner + '</div>';
  }

  // ─── Render: Mobile filter drawer ───────────────────────────────────
  // Resolves which facet data the drawer should list for the current tab —
  // 'all' and 'products' share the products index; recipes/blogs read their
  // own article facets. Mirrors buildSidebarHtml()/buildArticleSidebarHtml().
  function getDrawerFacetsInfo() {
    var scope = getFilterScope(currentTab);

    if (scope === 'recipes' || scope === 'blogs') {
      var articleData = tabData[scope];
      var articleRc = articleData && articleData.renderingContent;
      var articleRcOrder =
        articleRc && articleRc.facetOrdering && articleRc.facetOrdering.facets && articleRc.facetOrdering.facets.order;
      var explicitAttrs = config.articleFacets && config.articleFacets[0] !== '*' ? config.articleFacets : null;
      // Priority: (1) the Algolia dashboard's "Facet display" curation
      // (renderingContent.facetOrdering) — same source of truth the
      // products branch below already uses, so an admin turning facets off
      // there (leaving 0 attributesForFaceting selected, i.e. an explicit
      // empty order array) actually hides the mobile drawer's facet nav
      // instead of it silently falling back to "whatever came back on this
      // query". (2) the theme's own articleFacets setting, when it names
      // specific attributes. (3) the wildcard-config raw-facets fallback —
      // "whatever facets came back on this query" — same fallback
      // buildArticleSidebarHtml()/buildFacetSidebarHtml() use, so the
      // drawer and the inline sidebar never disagree about which facets
      // exist when neither of the above is configured. Without this,
      // ['*'] was used literally as the row order, and data.facets['*']
      // never matches anything — every row got silently skipped.
      var order =
        articleRcOrder || explicitAttrs || (articleData && articleData.facets ? Object.keys(articleData.facets) : []);
      var excluded = ARTICLE_ALWAYS_HIDDEN_FACETS.slice();
      if (scope === 'recipes') excluded.push('blog.title');
      order = order.filter(function (attr) {
        return excluded.indexOf(attr) === -1;
      });
      return { scope: scope, data: articleData, order: order };
    }

    var data = tabData.products;
    var rc = data && data.renderingContent;
    order =
      (rc && rc.facetOrdering && rc.facetOrdering.facets && rc.facetOrdering.facets.order) ||
      (data && data.facets ? Object.keys(data.facets) : []);
    return { scope: scope, data: data, order: order };
  }

  // True when the current tab/scope has at least one facet to show in the
  // mobile drawer's main panel. Used to hide the facet UI entirely (nav
  // list, filter icon/label, Clear All button) on tabs/queries that have
  // no facets — e.g. a Recipes/Blogs query with no article facets
  // configured, or a Products query where every facet came back empty —
  // so mobile visitors aren't shown a "FILTER AND SORT" button and an
  // empty drawer with nothing to filter by.
  function drawerHasFacets(info) {
    if (!info || !info.data || !info.data.facets || !info.order) return false;
    return info.order.some(function (attr) {
      return !!info.data.facets[attr];
    });
  }

  function renderDrawerMainPanel() {
    var info = getDrawerFacetsInfo();
    var data = info.data;
    var scopedFilters = activeFilters[info.scope] || {};

    if (!data || !data.facets) return '';

    var arrowSvg =
      '<svg class="algolia-drawer__nav-arrow" viewBox="0 0 8 14" width="8" height="14" aria-hidden="true">' +
      '<path d="M1 1l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var html = '<div class="algolia-drawer__nav">';
    info.order.forEach(function (attr) {
      if (!data.facets[attr]) return;
      var activeCount = scopedFilters[attr] ? scopedFilters[attr].length : 0;
      html +=
        '<button type="button" class="algolia-drawer__nav-row" data-open-facet="' +
        attr +
        '">' +
        '<span>' +
        toTitleCase(facetLabel(attr)) +
        '</span>' +
        (activeCount ? '<span class="algolia-drawer__nav-count">' + activeCount + '</span>' : '') +
        arrowSvg +
        '</button>';
    });
    html += '</div>';
    return html;
  }

  function renderDrawerFacetList(facetAttr, facetData, sortFn, scope) {
    var scopedFilters = activeFilters[scope] || {};
    var selected = scopedFilters[facetAttr] || [];
    var keys = Object.keys(facetData);
    // Disjunctive counts still apply every OTHER active filter, so a
    // selected value with zero matches under those won't come back in
    // facetData at all — keep it in the list so a checked box never
    // silently disappears from the drawer.
    selected.forEach(function (val) {
      if (keys.indexOf(val) === -1) keys.push(val);
    });
    if (sortFn) keys.sort(sortFn);

    var isExpanded = expandedFacets[facetAttr];
    var visibleKeys = isExpanded ? keys : keys.slice(0, FACET_VISIBLE_BY_DEFAULT);
    selected.forEach(function (val) {
      if (visibleKeys.indexOf(val) === -1) visibleKeys.push(val);
    });
    var hasMore = keys.length > FACET_VISIBLE_BY_DEFAULT;

    var html = '<ul class="algolia-filter-list algolia-drawer__facet-list">';
    visibleKeys.forEach(function (key) {
      var count = facetData[key] || 0;
      var isChecked = scopedFilters[facetAttr] && scopedFilters[facetAttr].indexOf(key) > -1;
      // A selected value must stay checkable (so it can be unchecked) even
      // if it currently has zero matches under the other active filters —
      // only disable a true zero-count value that isn't already selected.
      var isZero = count === 0 && !isChecked;
      var displayLabel =
        key.indexOf(':') !== -1 && facetAttr.indexOf('price') !== -1
          ? '$' + key.split(':')[0] + ' - $' + key.split(':')[1]
          : facetAttr.indexOf('price') !== -1 && !isNaN(parseFloat(key))
            ? '$' + parseFloat(key).toFixed(2)
            : key;
      html += '<li class="algolia-filter-item' + (isZero ? ' is-zero' : '') + '">';
      html += '<label class="algolia-filter-label">';
      html +=
        '<input type="checkbox" class="algolia-filter-checkbox" data-attr="' +
        facetAttr +
        '" data-value="' +
        escapeHtml(key) +
        '"' +
        (isChecked ? ' checked' : '') +
        (isZero ? ' disabled' : '') +
        '>';
      html += '<span>' + displayLabel + (SHOW_FACET_COUNTS ? ' (' + count + ')' : '') + '</span>';
      html += '</label>';
      html += '</li>';
    });
    html += '</ul>';

    if (hasMore) {
      var btnLabel = isExpanded ? '- Show less' : '+ Show more';
      html +=
        '<button type="button" class="algolia-filter-toggle" data-facet="' + facetAttr + '">' + btnLabel + '</button>';
    }
    return html;
  }

  function renderDrawerFacetPanel(facetAttr) {
    var info = getDrawerFacetsInfo();
    var data = info.data;
    if (!data || !data.facets || !data.facets[facetAttr]) return '';
    // Price-like facets sort numerically by their lower bound (e.g. "0:50"
    // before "50:100"); every other facet (Color, Category, Product Type,
    // ...) sorts alphabetically, matching the reference drawer's A-Z lists.
    var sortFn =
      facetAttr.indexOf('price') !== -1
        ? function (a, b) {
            return parseFloat(a.split(':')[0]) - parseFloat(b.split(':')[0]);
          }
        : function (a, b) {
            return a.toLowerCase().localeCompare(b.toLowerCase());
          };
    return renderDrawerFacetList(facetAttr, data.facets[facetAttr], sortFn, info.scope);
  }

  // Some Shopify themes render a section's markup more than once on the same
  // page (e.g. a hidden desktop-preview copy alongside the real mobile one,
  // or a duplicated app block), which means document.getElementById can
  // silently return the WRONG copy of an id — the button appears to render
  // fine but is a completely different DOM node than the one on screen, or
  // the drawer that gets toggled open is a hidden duplicate. getVisibleById
  // guards against that by preferring whichever matching element is actually
  // visible in the layout.
  function getVisibleById(id) {
    var all = document.querySelectorAll('#' + id);
    if (all.length === 0) return null;
    if (all.length === 1) return all[0];
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null) return all[i];
    }
    return all[0];
  }

  function buildMobileFilterButtonHtml() {
    var info = getDrawerFacetsInfo();
    var hasFacets = drawerHasFacets(info);
    return (
      '<button type="button" class="algolia-mobile-filter-button' +
      (hasFacets ? '' : ' algolia-mobile-filter-button--sort-only') +
      '">' +
      '<span class="algolia-mobile-filter-button__left">' +
      (hasFacets ? FILTER_ICON_SVG : FILTER_ICON_SVG) +
      '<span>' +
      (hasFacets ? 'FILTER AND SORT' : 'SORT BY') +
      '</span>' +
      '</span>' +
      '<span class="algolia-mobile-filter-button__count">' +
      formatItemCount(info.scope, info.data) +
      '</span>' +
      '</button>'
    );
  }

  function renderMobileFilterButton() {
    var container = getVisibleById('algolia-mobile-filter-btn');
    if (!container) {
      console.warn('[Algolia] #algolia-mobile-filter-btn mount element not found on the page.');
      return;
    }
    // Every tab that shows this button (All/Products/Recipes/Blogs) renders
    // it inline, right under the "results found" line (see renderAllTab/
    // renderProductsTab/renderRecipesTab/renderBlogsTab), so this static
    // slot above the tab bar is always kept empty.
    container.innerHTML = '';
  }

  // Delegated (bound once, on document) rather than re-attached on every
  // render — renderMobileFilterButton() runs on every tab switch, filter
  // apply, and resize, which was re-creating the button element each time.
  // A per-render addEventListener was fragile: if a click landed on the
  // button in the brief window between an old node being replaced and a new
  // listener being attached, the click was silently dropped. Delegation
  // means it always hits the current button, however it got there.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.algolia-mobile-filter-button');
    if (btn) {
      e.preventDefault();
      openMobileFilterDrawer();
    }
  });

  function ensureFilterDrawerElement() {
    var drawer = getVisibleById('algolia-mobile-filter-drawer');
    if (!drawer) {
      // Falls back to creating our own mount point when the theme's section
      // markup doesn't already include one — without this, a missing
      // #algolia-mobile-filter-drawer in the page HTML means the button
      // click handler runs, finds nothing, and silently no-ops.
      drawer = document.createElement('div');
      drawer.id = 'algolia-mobile-filter-drawer';
      drawer.className = 'algolia-mobile-filter-drawer';
      document.body.appendChild(drawer);
    }
    // If the theme's markup placed this element somewhere nested (e.g. inside
    // a smooth-scroll/page-transition wrapper), any ancestor with a CSS
    // transform/filter/perspective/will-change creates a new containing
    // block for position:fixed — silently trapping the drawer inside that
    // wrapper instead of the viewport, so it "opens" but isn't visible where
    // expected. Forcing it to be a direct child of <body> sidesteps that
    // regardless of what the rest of the theme does elsewhere on the page.
    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
    return drawer;
  }

  function openMobileFilterDrawer() {
    /*  var drawer = ensureFilterDrawerElement(); */

    /* injectFilterDrawerStyles(); */
    drawerPanel = 'main';
    drawerFacetAttr = null;
    renderDrawerBody();
  }

  function renderDrawerBody() {
    var drawer = ensureFilterDrawerElement();
    var info = getDrawerFacetsInfo();
    var scope = info.scope;
    var closeSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" width="24" height="24" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path></svg>';

    // When this tab/scope has no facets at all, the drawer is sort-only:
    // no filter icon, no "FILTER AND SORT" title (just "SORT BY"), no
    // facet-category nav list, and no "CLEAR ALL FILTERS" button since
    // there's nothing to clear. drawerPanel can never actually be 'facet'
    // in this state (there are no nav rows to open one from), but the
    // hasFacets check is still used below in case stale state lingers.
    var hasFacets = drawerHasFacets(info);

    var headerLeft;
    if (drawerPanel === 'facet' && drawerFacetAttr) {
      var backSvg =
        '<svg viewBox="0 0 8 14" width="8" height="14" aria-hidden="true"><path d="M7 1L1 7l6 6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      headerLeft =
        '<button type="button" class="algolia-drawer__back" aria-label="Back">' +
        backSvg +
        '</button>' +
        '<h3 class="algolia-drawer__title">' +
        toTitleCase(facetLabel(drawerFacetAttr)) +
        '</h3>';
    } else if (hasFacets) {
      headerLeft =
        '<span class="algolia-drawer__filter-icon">' +
        FILTER_ICON_SVG +
        '</span>' +
        '<h3 class="algolia-drawer__title">FILTER AND SORT</h3>';
    } else {
      headerLeft = '<h3 class="algolia-drawer__title">SORT BY</h3>';
    }
    var html = '<div class="algolia-drawer-overlay"></div>';
    html += '<div class="algolia-drawer' + (hasFacets ? '' : ' algolia-drawer--sort-only') + '">';
    html += '<div class="algolia-drawer__header">';
    html += '<div class="algolia-drawer__header-left">' + headerLeft + '</div>';
    html += '<span class="algolia-drawer__count">' + formatItemCount(scope, info.data) + '</span>';
    html += '<button type="button" class="algolia-drawer__close" aria-label="Close">' + closeSvg + '</button>';
    html += '</div>';

    html += '<div class="algolia-drawer__body">';
    if (hasFacets) {
      html += drawerPanel === 'facet' ? renderDrawerFacetPanel(drawerFacetAttr) : renderDrawerMainPanel();
    }
    if (drawerPanel === 'main' || !hasFacets) {
      html += '<div class="algolia-drawer__sort">' + renderSortDropdown(scope) + '</div>';
    }
    html += '<div class="algolia-drawer__footer">';
    html += '<button type="button" class="algolia-drawer__apply">APPLY</button>';
    if (hasFacets) {
      html += '<button type="button" class="algolia-drawer__clear">CLEAR ALL FILTERS</button>';
    }
    html += '</div>';
    html += '</div>';
    html += '</div>';

    drawer.innerHTML = html;
    drawer.classList.add('is-open');
    document.body.classList.add('no-scroll');

    bindDrawerEvents(drawer, scope);
  }

  function bindDrawerEvents(drawer, scope) {
    drawer.querySelector('.algolia-drawer__close').addEventListener('click', closeMobileFilterDrawer);
    drawer.querySelector('.algolia-drawer-overlay').addEventListener('click', closeMobileFilterDrawer);

    var backBtn = drawer.querySelector('.algolia-drawer__back');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        drawerPanel = 'main';
        drawerFacetAttr = null;
        renderDrawerBody();
      });
    }

    drawer.querySelectorAll('[data-open-facet]').forEach(function (row) {
      row.addEventListener('click', function () {
        drawerPanel = 'facet';
        drawerFacetAttr = row.getAttribute('data-open-facet');
        renderDrawerBody();
      });
    });

    // Filter checkboxes write straight into the current tab's own filter
    // bucket (never a shared global one) — same live-staging behavior as
    // the desktop sidebar; APPLY below is what actually triggers a reload.
    var scopedFilters = activeFilters[scope];
    var stagedApplies = []; // Track newly-checked facets for batch analytics on Apply

    drawer.querySelectorAll('.algolia-filter-checkbox').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var attr = cb.getAttribute('data-attr');
        var value = cb.getAttribute('data-value');
        if (!scopedFilters[attr]) scopedFilters[attr] = [];
        if (cb.checked) {
          scopedFilters[attr].push(value);
          // Track this apply for batch publishing when APPLY is pressed
          stagedApplies.push({ attr: attr, value: value });
        } else {
          scopedFilters[attr] = scopedFilters[attr].filter(function (v) {
            return v !== value;
          });
          // Remove from staged applies if user unchecked before pressing APPLY
          stagedApplies = stagedApplies.filter(function (item) {
            return !(item.attr === attr && item.value === value);
          });
        }
        if (scopedFilters[attr].length === 0) delete scopedFilters[attr];

        // Update Clear button state dynamically
        var clearBtn = drawer.querySelector('.algolia-drawer__clear');
        if (clearBtn) {
          var hasFilters = Object.keys(scopedFilters).length > 0;
          clearBtn.disabled = !hasFilters;
        }
      });
    });

    drawer.querySelectorAll('.algolia-filter-toggle').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var facet = btn.getAttribute('data-facet');
        expandedFacets[facet] = !expandedFacets[facet];
        renderDrawerBody();
      });
    });

    var sortSelect = drawer.querySelector('.algolia-sort__select');
    if (sortSelect) {
      var isDrawerSortOpen = false;
      var handleDrawerSortOpen = function () {
        if (isDrawerSortOpen) return;
        isDrawerSortOpen = true;
        publishClickEvent('productListing:sort', 'sortOpen', 'open');
      };

      sortSelect.addEventListener('mousedown', handleDrawerSortOpen);
      sortSelect.addEventListener('touchstart', handleDrawerSortOpen, { passive: true });
      sortSelect.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          handleDrawerSortOpen();
        }
      });
      sortSelect.addEventListener('blur', function () {
        isDrawerSortOpen = false;
      });

      sortSelect.addEventListener('change', function (e) {
        isDrawerSortOpen = false;
        currentSort[scope] = e.target.value;
      });
    }

    var applyBtn = drawer.querySelector('.algolia-drawer__apply');
    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        // Publish click event for each newly-applied facet
        stagedApplies.forEach(function (item) {
          var linkTitle = item.attr.toLowerCase() + '::' + String(item.value).toLowerCase();
          publishClickEvent('productListing:facet', linkTitle, 'filterApply');
        });

        closeMobileFilterDrawer();
        // Reload using `scope` — the same bucket the checkboxes above wrote
        // into via getFilterScope(currentTab) — so read and reload always
        // agree, and persist the URL like the desktop sidebar does.
        reloadTabAndRender(scope);
      });
    }

    var clearBtn = drawer.querySelector('.algolia-drawer__clear');
    if (clearBtn) {
      // Enable/disable based on active filters
      var hasFilters = activeFilters[scope] && Object.keys(activeFilters[scope]).length > 0;
      clearBtn.disabled = !hasFilters;

      clearBtn.addEventListener('click', function () {
        // Publish clear all filters event
        publishClickEvent('productListing:facet', 'clearAllFilters', 'filterClear');

        // Clear all filters and immediately apply (close drawer + reload)
        activeFilters[scope] = {};
        closeMobileFilterDrawer();
        reloadTabAndRender(scope);
      });
    }
  }

  function closeMobileFilterDrawer() {
    var drawer = document.getElementById('algolia-mobile-filter-drawer');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.innerHTML = '';
    }
    document.body.classList.remove('no-scroll');
    drawerPanel = 'main';
    drawerFacetAttr = null;
  }

  // ─── Bind: VIEW ALL links in ALL tab ────────────────────────────────

  function bindViewAllLinks(container) {
    container.querySelectorAll('[data-switch-tab]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        switchTab(link.getAttribute('data-switch-tab'), 'viewall');
        scrollToSearchTabs();
      });
    });
  }

  function scrollToSearchTabs() {
    var searchSection = document.getElementById('algolia-instantsearch');
    if (!searchSection) return;
    var headerHeight =
      parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    var top = searchSection.getBoundingClientRect().top + window.pageYOffset - headerHeight;
    window.scrollTo({ top: top, behavior: 'auto' });
  }

  // ALL's sort dropdown and pagination bind through the same
  // bindSortDropdown('products')/bindPagination() used by the PRODUCTS tab
  // (see renderAllTab's bind block) — no separate implementation, since the
  // two tabs share the same sort/page state.

  // ─── Init ───────────────────────────────────────────────────────────

  function initInstantSearch() {
    var container = document.getElementById('algolia-instantsearch');
    if (!container) return;

    var algoliaLib = window['algoliasearch/lite'];
    if (!algoliaLib) {
      console.warn('[Algolia] algoliasearch/lite not loaded. Check CDN scripts.');
      return;
    }

    searchClient = algoliaLib.liteClient(config.appId, config.searchOnlyApiKey);

    container.style.display = 'block';

    // Restore URL state (tab, filters, sort, page) before loading data
    restoreUrlState();

    updateSearchTitle(currentQuery);

    // ─── Defend against theme-level "scroll reveal" animations ─────────
    // Some Shopify themes hide grid items by default (opacity/height/
    // transform, sometimes set inline) and only reveal them via a one-time
    // IntersectionObserver scan on initial page load. Our cards are
    // injected later via innerHTML, so they can inherit that "not yet
    // revealed" state and never get the reveal trigger. The CSS override
    // on .hit-wrapper handles anything set via a stylesheet/class; this
    // observer catches the case where the theme sets inline styles
    // directly on the element (which beats any CSS selector, however
    // specific). Runs once per newly-added card, not on every mutation.
    var cardObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var cards = node.matches && (node.matches('.hit-wrapper, .hit') ? [node] : []);
          var found = (cards && cards.length ? cards : []).concat(
            Array.prototype.slice.call(node.querySelectorAll ? node.querySelectorAll('.hit-wrapper, .hit') : []),
          );
          found.forEach(function (card) {
            card.style.removeProperty('opacity');
            card.style.removeProperty('height');
            card.style.removeProperty('transform');
            card.style.removeProperty('visibility');
          });
        });
      });
    });
    cardObserver.observe(container, { childList: true, subtree: true });

    // 'error' doesn't bubble, so this has to run in the capture phase to
    // catch a broken page image anywhere under the container.
    container.addEventListener(
      'error',
      function (e) {
        var img = e.target;
        if (img && img.tagName === 'IMG' && img.closest('.hit-image--page') && img.src !== PLACEHOLDER_IMAGE) {
          img.src = PLACEHOLDER_IMAGE;
        }
      },
      true,
    );

    loadAndRenderCurrentState();

    // Handle resize
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var wasMobile = isMobile;
        isMobile = window.innerWidth < 750;
        if (wasMobile !== isMobile) {
          renderMobileFilterButton();
        }
      }, 200);
    });
  }

  // Shared load+render sequence for first page load and popstate.
  // stateLoadToken discards a stale response if a newer call starts first.
  var stateLoadToken = 0;
  function loadAndRenderCurrentState() {
    var token = ++stateLoadToken;
    return loadAllTabs().then(function () {
      if (token !== stateLoadToken) return;
      if (!isTabVisible(currentTab)) {
        // Prefer ALL, then PRODUCTS, whichever is actually visible for this
        // query; stay on ALL for a true zero-results-anywhere query.
        currentTab = isTabVisible('all') ? 'all' : isTabVisible('products') ? 'products' : 'all';
        persistUrlState();
      }
      renderTabs();
      renderMobileTabSelector();
      renderSidebar();
      renderMobileFilterButton();
      renderCurrentTab();
      renderCurrentRefinements();

      // Publish initial search analytics event
      // (after renderCurrentTab so updatePageTitle has run)
      publishSearchResultsEvent(getAnalyticsScope(currentTab));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInstantSearch);
  } else {
    initInstantSearch();
  }

  window.addEventListener('popstate', function () {
    if (window.location.pathname !== '/search') return;
    restoreUrlState();
    loadAndRenderCurrentState();
  });
})();
